/* eslint-disable no-useless-escape */
import { escapeHtml } from '@upsales/common';
import logError from 'App/babel/helpers/logError';
import T from 'Components/Helpers/translate';
import moment from 'moment';
import { loader } from 'App/helpers/googleMapsLoader';
import { getAvailableCountries } from 'App/services/prospectingService';
import appConfig from 'App/babel/config';
import AppService from 'App/legacy/AppService';
import CountryCodes from 'App/services/CountryCodes';
import { Feature, hasSoftDeployAccess, isAvailable } from 'App/services/FeatureHelperStandalone';
import EventBus from 'App/services/EventBus';
import BusinessEmail from 'App/babel/resources/BusinessEmail';
import DEFAULT_INVALID_DOMAINS from 'App/enum/invalidDomains';

// Interim per-customer allowlist (WEB-6545): custom landing-page domains approved to render the
// global invisible reCAPTCHA v3 key. Each must ALSO be registered on the v3 key in Google admin.
// Remove once the general multi-tenant custom-domain solution lands.
const APPROVED_V3_CUSTOM_DOMAINS = ['wise.se', 'wisegroup.se'];

// Lowercase on purpose: with REACT_COMPILER=true a capitalized const holding a function is
// treated as a component and the compiler segfaults on this 4,700-line parser (beta build).
const templateParser = (function () {
	var jQuery;
	var root = this; // this wil be window in the browser
	var _;
	var geocoder = null;

	// Check if the parser is used in node or browser
	// eslint-disable-next-line camelcase, no-undef
	if (typeof __webpack_require__ !== 'function' && process.env.NODE_ENV !== 'test') {
		jQuery = require('jquery');
		_ = require('lodash');
	} else {
		root = window;
		// Expose on browser window
		jQuery = root.jQuery;
		_ = root._;

		if (typeof jQuery === 'undefined') {
			jQuery = root[root.jQueryWindowPath];
		}
		if (typeof _ === 'undefined') {
			_ = root[root.lodashWindowPath];
		}
	}

	if (typeof jQuery === 'undefined') {
		// Need this for templateParser.test.js that is dependent of jQuery and lodash to exist outside of window
		// eslint-disable-next-line no-undef
		if (process.env.NODE_ENV === 'test') {
			jQuery = require('jquery');

			if (typeof _ === 'undefined') {
				_ = require('lodash');
			}
		}
		if (typeof jQuery === 'undefined') {
			throw new Error('BannerParser requires jQuery');
		}
	}

	if (typeof _ === 'undefined') {
		throw new Error('BannerParser requires lodash');
	}

	var selectors = {
		WRAPPER: '#wrapper',
		BODY: '[data-up-body]',
		ROW: '[data-up-row]',
		ELEMENT: '[data-up-element]',
		BACKGROUND: '[data-up-background]',
		HEAD: '[data-up-head]',
		COLUMN: '[data-up-col]',
		CONTENT: '[data-up-content]',
		CONTAINER: '.container'
	};

	var paddings = {
		NONE: 'none',
		XS: 'xs',
		SM: 'sm',
		MD: 'md',
		LG: 'lg',
		XL: 'xl',
		XXL: 'xxl'
	};

	var formTags = {
		CUSTOMER_ID: function (form, URL, customerId) {
			return customerId;
		},
		UUID: function (form) {
			return form.uuid;
		},
		IS_FRAME: function () {
			return 'false';
		},
		EXTERNAL_PATH: function (form, URL) {
			return URL + 'external/';
		},
		FORM_ACTION: function (form, URL) {
			// Embedded forms live on arbitrary hosts, so the submit must always go to
			// the absolute external URL — never the same-origin /forms/submit path used
			// for forms published on a custom domain (FIRST_PARTY_FORM_SUBMIT).
			// Intentionally diverges from the server-side FORM_ACTION resolver in
			// api/src/controller/customer/form.js: do NOT add a form.domain branch here.
			return URL + 'external/forms/submit';
		}
	};

	var Parser = {
		parse: async function (config, accountProfile, options, callback) {
			if (callback === undefined) {
				callback = options;
				options = {};
			}
			options = options || {};
			await parseFunction(config, accountProfile, false, options, callback);
		},
		parseDist: async function (config, accountProfile, options, callback) {
			if (callback === undefined) {
				callback = options;
				options = {};
			}
			options = options || {};
			await parseFunction(config, accountProfile, true, options, callback);
		},
		parseDist2: function (config, accountProfile, options, callback) {
			if (callback === undefined) {
				callback = options;
				options = {};
			}
			options = options || {};
			parseFunction(config, accountProfile, true, options, callback);
		},
		parseEmbedded: async function (config, accountProfile, options, callback) {
			if (callback === undefined) {
				callback = options;
				options = {};
			}
			options = options || {};
			options.embedded = true;

			var hasPhoneFields = config.fields.find(function (field) {
				return (field.name || '').toLowerCase().indexOf('phone') > -1;
			});

			await parseFunction(config, accountProfile, true, options, function (err, html) {
				if (hasPhoneFields) {
					html += getIntlTelLibrary();
					html += '<script>' + getTelInputInitScript() + '</script>';
				}

				var tags = html.match(/{{(.*?)}}/g);
				_.each(tags || [], function (tag) {
					var strippedTag = tag.replace('{{', '').replace('}}', '');
					if (!formTags[strippedTag]) {
						return;
					}
					var tagValue = formTags[strippedTag](config, Parser.URL, AppService.getCustomerId());
					if (tagValue) {
						html = html.replace(tag, tagValue);
					}
				});

				var hasOnlyBusinessEmailOn = _.find(config.fields, function (row) {
					return row.name === 'Prefs.verifyEmailDomain';
				});

				if (hasOnlyBusinessEmailOn) {
					html += Parser.businessEmailScript || '';
				} else {
					html += getSubmitScript();
				}

				callback(null, html);
			});
		},
		generateConfig: generateConfig,
		getBlankMarkdown: getBlankMarkdown,
		clearColumn: clearColumn,
		deleteRow: deleteRow,
		addRow: addRow,
		setColumnType: setColumnType,
		getElementConfig: getElementConfig,
		moveRowUp: moveRow('up'),
		moveRowDown: moveRow('down'),
		translate: function (t) {
			return t;
		},
		URL: '',
		invalidDomains: [],
		generateSocialMediaTagsObject: generateSocialMediaTagsObject,
		_getGmapKey: getGmapKey
	};

	var getColorProp = function (profile, propName) {
		return profile.colorsLandingPages ? profile.colorsLandingPages[propName] : profile.colors[propName];
	};

	var tagsMap = {
		'profile.name': function (profile) {
			return profile.name;
		},
		'profile.phone': function (profile) {
			return profile.phone;
		},
		'profile.email': function (profile) {
			return profile.email;
		},
		'profile.address': function (profile) {
			return profile.address;
		},
		'profile.zipcode': function (profile) {
			return profile.zipcode;
		},
		'profile.city': function (profile) {
			return profile.city;
		},
		'profile.country': function (profile) {
			return profile.country;
		},
		'profile.logo': function (profile) {
			return profile.logo;
		},
		'profile.darkLogo': function (profile) {
			return profile.darkLogo;
		},
		'profile.colors.bodyBg': function (profile) {
			return getColorProp(profile, 'bodyBg');
		},
		'profile.colors.buttonBg': function (profile) {
			return getColorProp(profile, 'buttonBg');
		},
		'profile.colors.buttonText': function (profile) {
			return getColorProp(profile, 'buttonText');
		},
		'profile.colors.contentBg': function (profile) {
			return getColorProp(profile, 'contentBg');
		},
		'profile.colors.contentText': function (profile) {
			return getColorProp(profile, 'contentText');
		},
		'profile.colors.contentTextIngress': function (profile) {
			return getColorProp(profile, 'contentTextIngress');
		},
		'profile.colors.footerLinkText': function (profile) {
			return getColorProp(profile, 'footerLinkText');
		},
		'profile.colors.footerText': function (profile) {
			return getColorProp(profile, 'footerText');
		},
		'profile.colors.linkText': function (profile) {
			return getColorProp(profile, 'linkText');
		},
		'profile.colors.mainBg': function (profile) {
			return getColorProp(profile, 'mainBg');
		},
		'profile.colors.mainText': function (profile) {
			return getColorProp(profile, 'mainText');
		},
		'profile.colors.secondaryText': function (profile) {
			return getColorProp(profile, 'secondaryText');
		}
	};

	var setElemText = function (elem, html) {
		var contentElem = elem.find(selectors.CONTENT);

		if (contentElem.length) {
			contentElem.html(html);
			contentElem.removeAttr('data-up-content');
		} else {
			elem.html(html);
		}
	};

	var getCountdownUnit = function (unit, date, value, unitText) {
		var elem = jQuery('<div />');
		elem.addClass('cd-unit ' + unit);
		var header = jQuery('<div />');
		header.addClass('cd-header');
		if (unitText) {
			header.text(unitText);
		} else {
			header.text(Parser.translate('date.' + unit));
		}

		if (value.textColor) {
			header.css('color', value.textColor);
		}

		elem.append(header);
		var num = jQuery('<h2 />');
		num.text('00');

		if (value.numberColor) {
			num.css('color', value.numberColor);
		}

		if (date) {
			var ms = moment(date).unix();
			var curr = moment().unix();
			var diffTime = ms - curr;
			var duration = moment.duration(diffTime * 1000, 'milliseconds');
			var number = duration[unit]();

			if (unit === 'days') {
				number = moment(date).diff(moment(), 'days');
			}

			if (number < 0) {
				number = 0;
			}
			if (number.toString().length === 1) {
				number = '0' + number;
			}
			num.text(number);
		}

		elem.append(num);

		return elem;
	};

	var getPlaceholder = function (title, body, iconClass, iconAsImg) {
		var placeholder = jQuery('<div />');
		placeholder.addClass('up-editor-placeholder');

		if (iconClass) {
			if (iconAsImg) {
				var img = jQuery('<img />');
				img.attr('src', 'img/' + iconClass);
				img.addClass('up-editor-placeholder-img');
				placeholder.append(img);
			} else {
				var icon = jQuery('<b />');
				icon.addClass(iconClass);
				placeholder.append(icon);
			}
		}

		var titleElem = jQuery('<div />');
		titleElem.addClass('editor-placeholder-text').text(title);
		placeholder.append(titleElem);

		if (body) {
			var bodyElem = jQuery('<h3 />');
			bodyElem.addClass('editor-placeholder-body').text(body);
			placeholder.append(bodyElem);
		}

		return placeholder;
	};

	var getYouTubeUrl = function (url) {
		var base = 'https://www.youtube.com/embed/';
		var splt;
		if (url.indexOf('youtube.com/watch?v=') !== -1) {
			splt = url.split('youtube.com/watch?v=');
			return base + splt[splt.length - 1];
		} else if (url.indexOf('youtu.be/') !== -1) {
			splt = url.split('youtu.be/');
			return base + splt[splt.length - 1];
		}
		return '';
	};

	var getVimeoUrl = function (url) {
		var base = 'https://player.vimeo.com/video/';
		var re = /vimeo.com\/([0-9]+)/g;
		var match = re.exec(url);
		return match ? base + match[1] : '';
	};

	var parseCountdown = function (elem, config, date, dateTag) {
		elem.css('background-color', config.value.backgroundColor);
		if (dateTag) {
			elem.attr('data-date-tag', dateTag);
		}
		if (date && moment(date).isValid()) {
			elem.attr('data-date', new Date(date).toJSON());
		}
		const dUnit = getCountdownUnit('days', date, config.value, config.unitText?.days);
		const hUnit = getCountdownUnit('hours', date, config.value, config.unitText?.hours);
		const mUnit = getCountdownUnit('minutes', date, config.value, config.unitText?.minutes);
		elem.append(dUnit);
		elem.append(hUnit);
		elem.append(mUnit);
		elem.append(elem);
	};

	var getLocaionData = function (row, opts, cb) {
		var liveTags = opts.liveTags || {};
		var hasLocationTag = row.some(function (r) {
			return r.columns.some(function (c) {
				return c.elements.some(function (e) {
					return e.value && e.value.address === '{{Event.Location}}';
				});
			});
		});
		if (hasLocationTag && liveTags['Event.Location']) {
			var geoInput = { address: liveTags['Event.Location'].value };

			loader.load().then(
				() => {
					if (!geocoder) {
						geocoder = new google.maps.Geocoder();
					}

					geocoder.geocode(geoInput, function (results) {
						if (results && results.length) {
							var location = results[0].geometry.location;
							opts.locationData = {
								address: geoInput.address,
								lat: location.lat(),
								lng: location.lng()
							};
						}
						cb();
					});
				},
				e => {
					logError(e, 'Failed to load Google');
					cb();
				}
			);
		} else {
			return cb();
		}
	};

	function getGmapKey(domain) {
		if (!domain) {
			return appConfig.STATIC_MAPS_KEY;
		}
		return domain.gmapKey;
	}

	var parseElement = function (elem, id, config, hasParent, isDist, form, opts) {
		if (!config) {
			return;
		}

		// Find the used domain
		var domain;
		if (opts.domains && form.domain) {
			domain = _.find(opts.domains, { name: form.domain });
		}
		var placeholder = null;
		const setVideoPlaceholder = () => {
			placeholder = getPlaceholder(Parser.translate('landingpage.enterUrlForVideo'), null, 'fa fa-video-camera');
		};

		elem.attr('id', id);
		elem.removeAttr('data-up-id');
		elem.removeAttr('data-up-lang');
		elem.removeAttr('data-up-element');
		elem.removeAttr('data-src');

		if (!opts.embedded) {
			elem.addClass('up-element');
			elem.addClass('type-' + config.type);
		}

		// check for parent
		if (hasParent) {
			elem.parent().addClass('up-element-parent');
		}
		switch (config.type) {
			case 'text':
				setElemText(elem, inheritStylesFromChildren(translateLiveTags(config.value.text, opts.liveTags)));
				break;
			case 'image':
				var value = config.value.src;
				var linkHref = config.linkHref;
				var target = config.linkTarget;
				if (value && value.length) {
					elem.attr('src', value);
					if (config.value.opacity !== undefined) {
						var parsed = parseInt(config.value.opacity);
						if (!isNaN(parsed)) {
							elem.css('opacity', parsed / 100);
						}
					}
					switch (config.value.align) {
						case 'left':
						case 'right':
							elem.css('float', config.value.align);
							break;
						case 'center':
							elem.css({
								margin: '0 auto',
								display: 'block'
							});
							break;
					}

					if (config.value.width) {
						elem.css('width', config.value.width);
						if (elem.attr('id') === 'logo') {
							elem.css('max-height', 'unset');
						}
					} else if (config.value.maxWidth) {
						elem.css('max-width', config.value.maxWidth);
						if (elem.attr('id') === 'logo') {
							elem.css('max-height', 'unset');
						}
					}

					if (linkHref && linkHref.length) {
						elem.wrap(function () {
							if (target && target === '_blank') {
								return '<a style="float:left;" target="' + target + '" href=' + linkHref + '></a>';
							}
							return '<a style="float:left;" href=' + linkHref + '></a>';
						});
					}
				} else if (!isDist) {
					placeholder = getPlaceholder(Parser.translate('bannerEditor.setImage'), null, 'fa fa-image');
				}

				elem.after('<div style="clear: both"></div>');

				break;

			case 'countdown':
				var date;
				if (config.value.dateTag) {
					date = translateLiveTags(config.value.dateTag, opts.liveTags);
				} else {
					date = config.value.date;
				}
				if (date && moment(date).isValid()) {
					parseCountdown(elem, config, date, config.value.dateTag);
				} else if (!isDist) {
					placeholder = getPlaceholder(
						Parser.translate('landingpage.enterDateForCountdown'),
						null,
						'fa fa-sort-numeric-desc'
					);
				} else if (opts.isPreview) {
					parseCountdown(elem, config, date, config.value.dateTag);
				}
				break;

			case 'button':
				var h2 = jQuery('<h2/>');
				h2.css('color', config.value.titleColor);
				h2.text(config.value.title);
				elem.append(h2);

				var linkButton = jQuery('<a class="btn" />');
				linkButton.css({
					color: config.value.buttonTextColor,
					'background-color': config.value.buttonBackgroundColor,
					padding: config.value.paddingV + 'px ' + config.value.paddingH + 'px',
					'font-size': config.value.fontSize + 'px'
				});

				linkButton.attr({
					href: config.value.link,
					target: config.value.srcTarget
				});

				if (config.value.align === 'center') {
					linkButton.removeClass('pull-left');
					linkButton.removeClass('pull-right');

					linkButton.css({
						'margin-left': 'auto',
						'margin-right': 'auto',
						display: 'table'
					});
				} else if (config.value.align === 'right') {
					linkButton.removeClass('pull-left');
					linkButton.addClass('pull-right');
				} else {
					linkButton.removeClass('pull-right');
					linkButton.addClass('pull-left');
				}

				if (config.value.fullWidth) {
					linkButton.addClass('btn-block');
				} else {
					linkButton.removeClass('btn-block');
				}

				linkButton.html(config.value.text);
				elem.append(linkButton);
				break;

			case 'file':
				if (config.value.src && !config.value.asImg) {
					if (config.value.title) {
						var titleELem = jQuery('<h2/>');
						titleELem.css({
							color: config.value.textColor
						});
						titleELem.text(config.value.title);
						elem.append(titleELem);
					}
					var button = jQuery('<a class="up-file-btn" />');
					button.attr({
						href: config.value.src,
						target: '_blank'
					});
					button.css({
						color: config.value.buttonTextColor,
						'background-color': config.value.buttonBackgroundColor
					});
					button.html('<b class="fa fa-download"></b> ' + config.value.buttonText);
					elem.append(button);
				} else if (config.value.src && config.value.imgSrc && config.value.asImg) {
					var link = jQuery('<a class="up-file-img-link" />');
					link.attr({
						href: config.value.src,
						target: '_blank'
					});
					var fileImg = jQuery('<img />');
					fileImg.attr('src', config.value.imgSrc);
					link.append(fileImg);
					elem.append(link);
				} else if (!isDist) {
					var msg = config.value.asImg
						? Parser.translate('bannerEditor.setImage')
						: Parser.translate('landingpage.addFileForDownload');
					placeholder = getPlaceholder(msg, null, 'fa fa-download');
				}
				break;
			case 'video':
				var src = config.value.src;
				elem.attr('data-src', src);

				var iframe;
				var params = [];
				if (src.indexOf('youtube') !== -1 || src.indexOf('youtu.be') !== -1) {
					var url = getYouTubeUrl(src);
					if (!url) {
						setVideoPlaceholder();
						break;
					}

					elem.attr('data-type', 'youtube');
					elem.addClass('youtube-video');

					iframe = jQuery('<iframe type="text/html">');

					params.push('showinfo=0');

					if (config.allowFullScreen || config.value?.allowFullScreen) {
						params.push('fs=1');
						iframe.attr('allowfullscreen', '');
					} else {
						params.push('fs=0');
					}

					params = '?' + params.join('&');

					iframe.css({
						width: '100%'
					});

					iframe.attr({
						width: 720,
						height: 405,
						src: url + params,
						frameborder: 0
					});

					if (config.value.shadow) {
						elem.addClass('shadow');
					}
				} else if (src.indexOf('vimeo') !== -1) {
					src = getVimeoUrl(src);
					if (!src) {
						setVideoPlaceholder();
						break;
					}

					elem.attr('data-type', 'vimeo');
					elem.addClass('vimeo-video');
					iframe = jQuery('<iframe type="text/html">');

					iframe.css({
						width: '100%'
					});

					iframe.attr({
						width: 720,
						height: 405,
						src: src,
						frameborder: 0
					});
				} else if (src.indexOf('wistia') !== -1) {
					elem.attr('data-type', 'wistia');
					elem.addClass('wistia-video');

					var getWistiaIdFromVideo = function (url) {
						if (url) {
							var id = url.split('/medias/')[1];
							if (id && id.length) {
								return id;
							}
						}

						return '';
					};

					var wistiaId = getWistiaIdFromVideo(src);
					if (wistiaId) {
						var videoHolder = jQuery('<div>');
						videoHolder.append(
							'<script src="https://fast.wistia.com/embed/medias/' + wistiaId + '.jsonp" async></script>'
						);
						videoHolder.append(
							'<script src="https://fast.wistia.com/assets/external/E-v1.js" async></script>'
						);
						videoHolder.append(
							'<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><div class="wistia_embed wistia_async_' +
								wistiaId +
								' videoFoam=true" style="height:100%;position:relative;width:100%"><div class="wistia_swatch" style="height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;"><img src="https://fast.wistia.com/embed/medias/' +
								wistiaId +
								'/swatch" style="filter:blur(5px);height:100%;object-fit:contain;width:100%;" alt="" onload="this.parentNode.style.opacity=1;" /></div></div></div></div>'
						);

						elem.append(videoHolder);
					} else {
						setVideoPlaceholder();
					}
				} else if (!isDist) {
					setVideoPlaceholder();
				}

				elem.append(iframe);

				break;
			case 'form':
				var wrapper = jQuery('<div />').addClass('up-form-wrapper');
				var formElem = jQuery('<form />').attr('id', 'up-form');
				var thanksElem = jQuery('<div />').attr('id', 'up-form-thanks');
				var thanksElemOnDemand = jQuery('<div />').attr('id', 'up-form-thanks-on-demand');
				var eventCanceledElem = jQuery('<div />').attr('id', 'up-event-canceled');
				var eventPassedElem = jQuery('<div />').attr('id', 'up-event-passed');
				var eventFullElem = jQuery('<div />').attr('id', 'up-form-event-full');
				var waitingListBtn = jQuery('<button />')
					.attr('id', 'up-waiting-list-btn')
					.text('Sign up for the waiting list');
				var reCaptcha = false;
				var doubleOptIn = false;
				var thankConfig = form.thankYouElement;
				var thankConfigOnDemand = form.thankYouElementOnDemand;
				var el = null;

				if (!opts.embedded) {
					waitingListBtn.addClass('btn btn-primary btn-block');
					waitingListBtn.css('border', 0);
				}

				if (!isDist) {
					var cover = jQuery('<div class="up-form-wrapper-cover" />');
					wrapper.append(cover);
				}

				var onSubmit = 'return validateForm(event)';
				(function () {
					const generateThankYouElement = (thanksElem, thankConfig, thankYouTitle, thankYouText) => {
						let formPlaceholder = null;
						if (thankConfig) {
							if (thankConfig.type === 'landingPage') {
								onSubmit = 'return validateForm(event, "' + thankConfig.value.href + '")';

								if (!isDist) {
									if (!thankConfig.value.href || !thankConfig.value.href.length) {
										// Show placeholder
										formPlaceholder = getPlaceholder(
											Parser.translate('form.selectExternalSite'),
											null,
											'fa fa-globe'
										);
									} else {
										// Show placeholder
										formPlaceholder = getPlaceholder(thankConfig.value.href, null, 'fa fa-globe');
									}
								}
							} else {
								if (thankConfig.type === 'image') {
									el = jQuery('<img />');
								} else {
									el = jQuery('<div />');
								}
								formPlaceholder = parseElement(
									el,
									thankConfig.id,
									thankConfig,
									false,
									isDist,
									form,
									opts
								);
								thanksElem.append(el);
							}
						}

						if (el && formPlaceholder) {
							el.css('display', 'none');
						}

						if (config && config.type === 'form' && (!thankConfig || thankConfig.type !== 'text')) {
							var titleWrapper = jQuery('<div />');

							if (thankYouTitle) {
								const titleELem = jQuery('<h2/>');
								titleELem.css({
									lineHeight: '36px',
									marginBottom: '10px'
								});
								titleELem.text(thankYouTitle);
								titleWrapper.append(titleELem);
							}

							if (thankYouText) {
								var decrElem = jQuery('<p/>');
								decrElem.css({
									lineHeight: '24px'
								});
								decrElem.text(thankYouText);
								titleWrapper.append(decrElem);
							}

							thanksElem.append(titleWrapper).css({ textAlign: 'center' });
						}

						return formPlaceholder;
					};

					let thankYouPlaceholder = generateThankYouElement(
						thanksElem,
						thankConfig,
						form.thankYouTitle,
						form.thankYouText
					);

					if (!isDist) {
						// Show placeholder
						thankYouPlaceholder = getPlaceholder(
							Parser.translate('landingpage.whenSubmittedShows'),
							Parser.translate('landingpage.whenSubmittedShowsInfo'),
							'thankyou-page-icon.png',
							true
						);
					}

					if (thankYouPlaceholder) {
						thanksElem.append(thankYouPlaceholder);
					}

					const thankYouODPlaceholder = generateThankYouElement(
						thanksElemOnDemand,
						thankConfigOnDemand,
						form.thankYouTitleOnDemand,
						form.thankYouTextOnDemand
					);

					if (thankYouODPlaceholder) {
						thanksElemOnDemand.append(thankYouODPlaceholder);
					}

					eventCanceledElem.append('<span>Sorry, this event is canceled</span>');
					eventPassedElem.append('<span>Sorry, this event is no longer available</span>');

					eventFullElem.append('<span id="up-event-full-text">Sorry, this event is full</span>');
					eventFullElem.append(waitingListBtn);

					// FORM_ACTION is resolved server-side at save: same-origin /forms/submit for
					// forms on a custom domain (so AdBlock doesn't cancel the cross-domain POST),
					// otherwise the absolute external submit URL as before.
					let action = '{{FORM_ACTION}}';
					if (!hasSoftDeployAccess('FIRST_PARTY_FORM_SUBMIT')) {
						action = '{{EXTERNAL_PATH}}forms/submit';
					}
					formElem.attr({
						name: 'form_{{UUID}}',
						action,
						method: 'POST',
						onsubmit: !opts.embedded ? onSubmit : undefined
					});

					if (!opts.embedded) {
						formElem.add(thanksElem);
						formElem.add(thanksElemOnDemand);
					}

					if (form.title && form.title !== '') {
						var title = jQuery('<h2/>').text(translateLiveTags(form.title, opts.liveTags));
						formElem.append(title);
					}

					if (form.description && form.description !== '') {
						var description = jQuery('<p/>').text(translateLiveTags(form.description, opts.liveTags));
						formElem.append(description);
					}

					// if form failed msg
					if (!opts.embedded) {
						formElem.append('<span id="failedId" style="color:red;"></span>');
					}

					_.sortBy(form.fields, 'sort').forEach((f, index) => {
						f = Object.assign({}, f); // so we can temporarilylyly set datatype (for country for example)
						if (!f.name) {
							return;
						}
						if (f.name === 'Prefs.reCaptcha') {
							reCaptcha = f;
							return;
						}
						if (f.name === 'Prefs.doubleOptIn') {
							doubleOptIn = f;
							return;
						}
						if (f.name.indexOf('Prefs.') === 0) {
							return;
						}
						// Fix for old forms where country field is saved as type=text
						if (f.name === 'Client.country') {
							f.datatype = 'select';
							f.options = [].join(','); // must exist
						}
						var fg = jQuery('<div />');
						if (!opts.embedded) {
							fg.addClass('form-group').attr('data-name', f.name);
						} else {
							fg.append('\n');
						}

						var label;
						if (f.datatype !== 'optIn' && f.datatype !== 'info') {
							label = jQuery('<label />').text(f.title);

							if (f.required && opts.embedded) {
								label.html(label.html() + ' *');
							} else if (f.required) {
								label.html(label.html() + ' <span class="text-danger">*</span>');
							}

							label.html(label.html().replace('&amp;', '&'));
							fg.append(label);
						}

						var options = f.options;
						var field;
						var isMultipleElements = false;
						if (!f.datatype || f.datatype === 'text') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<input />');

							var attr = {
								maxlength: 8192,
								type: 'text',
								placeholder: f.placeholder?.replaceAll('&amp;', '&')
							};

							if (f.name === 'Contact.firstname') {
								attr.id = 'up-firstname-input';
								attr.autocomplete = 'given-name';
							}

							if (f.name === 'Contact.lastname') {
								attr.id = 'up-lastname-input';
								attr.autocomplete = 'family-name';
							}

							if (f.name === 'Contact.email') {
								attr.type = 'email';
								// prettier-ignore
								attr.pattern = "^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+){1,}$";
								attr.title = 'Please enter a valid email';
								fg.addClass('email');
								attr.id = 'up-email-input';
								attr.autocomplete = 'email';
							}

							if (f.name === 'Client.name') {
								attr.id = 'up-client-name-input';
								attr.autocomplete = 'organization';
							}

							field.attr(attr);
						} else if (f.datatype === 'textarea') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<textarea />');
							field.attr({
								maxlength: 8192,
								rows: 3,
								placeholder: f.placeholder?.replaceAll('&amp;', '&')
							});
						} else if (f.datatype === 'date') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<input />');
							field.attr({
								type: 'date',
								placeholder: f.placeholder
							});
						} else if (f.datatype === 'number') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<input />');
							field.attr({
								type: 'number',
								placeholder: f.placeholder
							});
						} else if (f.datatype === 'password') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<input />');
							field.attr({
								type: 'password',
								placeholder: f.placeholder
							});
						} else if (f.datatype === 'checkbox') {
							if (opts.embedded) {
								fg.append('\n<br/>');
							} else {
								fg.append('<br/>');
							}
							if (!options) {
								options = '';
							} else {
								options = options.split(',');
							}

							isMultipleElements = true;

							if (!options) {
								var checkbox = jQuery('<input />');
								checkbox.attr({
									type: 'checkbox',
									value: 'on',
									name: f.name
								});

								if (f.required) {
									checkbox.attr('required', 'required');
								}
								label.prepend(' ');
								label.prepend(checkbox);
							} else {
								options.forEach(function (option) {
									var o = decodeURIComponent(option).trim();
									if (!o.length) {
										return;
									}
									if (opts.embedded) {
										fg.append('\n');
									}
									var l = jQuery('<label/>');
									var c = jQuery('<input/>');
									c.attr({
										type: 'checkbox',
										value: o,
										name: f.name
									});
									if (!opts.embedded) {
										l.css({
											'font-weight': 'normal',
											'margin-right': '5px'
										});
									}
									l.append(c);
									l.append('&nbsp;' + o + '&nbsp;');
									fg.append(l);
								});
							}
						} else if (f.datatype === 'radio' && options) {
							options = options.split(',');
							if (options.length) {
								if (opts.embedded) {
									fg.append('\n<br/>');
								} else {
									fg.append('<br/>');
								}

								isMultipleElements = true;

								options.forEach(function (option) {
									var o = decodeURIComponent(option).trim();
									if (!o.length) {
										return;
									}
									if (opts.embedded) {
										fg.append('\n');
									}
									var l = jQuery('<label/>');
									var r = jQuery('<input/>');
									r.attr({
										type: 'radio',
										value: o,
										name: f.name
									});
									if (!opts.embedded) {
										l.css({
											'font-weight': 'normal',
											'margin-right': '5px'
										});
									}
									if (f.required) {
										r.attr('required', 'required');
									}
									l.append(r);
									l.append('&nbsp;' + o + '&nbsp;');
									fg.append(l);
								});
							}
						} else if (f.datatype === 'select' && (options || f.name === 'Client.country')) {
							if (f.name === 'Client.country') {
								const countries = CountryCodes.countries();
								const [language] = (AppService.getSelf().language ?? '').split('-');
								options = countries
									.map(option => ({
										...option,
										lang: T(option.lang)
									}))
									.sort((a, b) => a.lang.localeCompare(b.lang, language || undefined));
							} else {
								options = options.split(',');
							}
							if (!options.length) {
								return;
							}
							field = jQuery('<select />');
							if (opts.embedded) {
								field.append('\n');
							}

							if (!f.required) {
								field.append('<option></option>');
							}
							if (opts.embedded) {
								field.append('\n');
							}
							options.forEach(function (option) {
								if (f.name === 'Client.country') {
									field.append('<option value="' + option.code + '">' + option.lang + '</option>');
								} else {
									var o = decodeURIComponent(option).trim();
									if (!o.length) {
										return;
									}
									const opt = jQuery('<option />');
									opt.text(o);
									opt.attr('value', o);
									field.append(opt);
								}
								if (opts.embedded) {
									field.append('\n');
								}
							});
						} else if (f.datatype === 'multiselect' && options) {
							options = options.split(',');
							if (!options.length) {
								return;
							}
							field = jQuery('<select />');
							field.attr('multiple', 'multiple');
							if (opts.embedded) {
								field.append('\n');
							}
							options.forEach(function (option) {
								var o = decodeURIComponent(option).trim();
								if (!o.length) {
									return;
								}
								const opt = jQuery('<option />');
								opt.text(o);
								opt.attr('value', o);
								field.append(opt);
								if (opts.embedded) {
									field.append('\n');
								}
							});
						} else if (f.datatype === 'optIn' && f.options) {
							var optIn = _.find(opts.optIns || [], { id: parseInt(f.options, 10) });
							if (optIn) {
								var optInBox = jQuery('<input />');
								fg.addClass('form-group');
								optInBox.attr({
									type: 'checkbox',
									value: 'on',
									name: f.name,
									style: 'margin: auto 7px auto 0px;'
								});

								if (f.required) {
									optInBox.attr('required', 'required');
								}

								// Parse links in text, for preview, remove astrisses and place text in a-tag
								var regex = /\{\{(.*)\}\}/;
								var optInHtml = optIn.text;
								var match = optInHtml.match(regex);
								if (optIn.terms) {
									if (match) {
										// Link word selected with {{ }}
										optInHtml = optInHtml.replace(
											regex,
											'<a class="up-unstyled-element"' +
												(isDist ? 'href="javascript:openTerms(' + f.options + ');"' : '') +
												'>' +
												match[1] +
												'</a>'
										);
									} else {
										// Link whole text
										optInHtml =
											'<a class="up-unstyled-element"' +
											(isDist ? 'href="javascript:openTerms(' + f.options + ');"' : '') +
											'>' +
											optInHtml +
											'</a>';
									}
								} else {
									// Dont link to terms if none exists
									optInHtml = optInHtml.replace('{{', '').replace('}}', '');
								}

								label = jQuery('<label class="opt-in-label" />').html(optInHtml);

								if (f.required) {
									label.html(label.html() + ' <span class="text-danger">*</span>');
								}
								fg.prepend(' ');
								fg.prepend(label);
								fg.prepend(optInBox);
								fg.css('display', 'flex');
							}
						} else if (f.datatype === 'email') {
							if (opts.embedded) {
								fg.append('\n<br/>\n');
							}
							field = jQuery('<input />');

							const attr = {
								maxlength: 8192,
								type: 'text',
								placeholder: f.placeholder
							};

							attr.type = 'email';
							// prettier-ignore
							attr.pattern = "^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+){1,}$";
							attr.title = 'Please enter a valid email';
							fg.addClass('email');
							attr.id = 'up-email-input';
							attr.autocomplete = 'email';

							field.attr(attr);
						} else if (f.datatype === 'file') {
							isMultipleElements = true;
							var groups = (f.options || 'images,documents').split(',');
							var groupExt = {
								images: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
								documents: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.ppt', '.pptx']
							};
							var accept = groups
								.map(function (g) {
									return groupExt[g.trim()] || [];
								})
								.reduce(function (a, b) {
									return a.concat(b);
								}, [])
								.join(',');
							var fileInput = jQuery('<input />').attr({
								type: 'file',
								id: 'up-file-input-' + index,
								accept: accept
							});
							if (!opts.embedded) {
								fileInput.addClass('form-control');
							}
							if (f.required) {
								fileInput.attr('required', 'required');
							}
							var tokenInput = jQuery('<input />').attr({
								type: 'hidden',
								name: f.name,
								id: 'up-file-token-' + index
							});
							fg.append(fileInput);
							fg.append(tokenInput);
						} else if (f.datatype === 'info' && f.info) {
							fg.append(jQuery(`<div style="word-break: break-word;">${f.info}</div>`));
						}

						if (!isMultipleElements && field) {
							if (!opts.embedded) {
								field.addClass('form-control');
							} else if (f.datatype === 'select' || f.datatype === 'multiselect') {
								fg.append('\n<br/>\n');
							}

							field.attr('name', f.name);

							if (f.required) {
								field.attr('required', 'required');
							}

							fg.append(field);
						}

						if (opts.embedded) {
							fg.append('\n');
						}

						formElem.append(fg);

						if (opts.embedded) {
							formElem.append('\n');
						}
					});

					if (isDist && !opts.noTags) {
						if (opts.embedded) {
							formElem.append('<!-- REQUIRED FIELDS -->\n');
						}
						formElem.append('<input type="hidden" name="formCid" value="{{CUSTOMER_ID}}" />');
						if (opts.embedded) {
							formElem.append('\n');
						}
						formElem.append('<input type="hidden" name="formId" value="{{UUID}}" />');
						if (opts.embedded) {
							formElem.append('\n');
						}
						formElem.append('<input type="hidden" name="isFrame" value="{{IS_FRAME}}" />');
						if (opts.embedded) {
							formElem.append('\n');
						}
						var validation = jQuery('<input />');
						validation.css('display', 'none');
						validation.attr({
							type: 'text',
							value: '',
							name: 'validation'
						});
						formElem.append(validation);

						if (opts.embedded) {
							formElem.append('<!-- END OF REQUIRED FIELDS -->\n');
						}

						if (reCaptcha && !opts.embedded) {
							// v3 for Upsales-hosted forms (no custom domain) + explicitly-approved custom
							// domains (APPROVED_V3_CUSTOM_DOMAINS, also registered on the v3 key in Google).
							// Other custom domains stay v2: unregistered domains can't use the global v3 key.
							if (
								hasSoftDeployAccess('FORM_CAPTCHA_V3') &&
								(!domain || APPROVED_V3_CUSTOM_DOMAINS.includes(domain.name))
							) {
								formElem.append('<input type="hidden" name="Prefs.reCaptchaMode" value="v3" />');
								formElem.append('<input type="hidden" name="Prefs.reCaptcha" value="" />');
								formElem.append(
									'<p style="font-size:11px;color:#888;margin-top:10px;">' +
										'This site is protected by reCAPTCHA and the Google ' +
										'<a href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a> and ' +
										'<a href="https://policies.google.com/terms" target="_blank">Terms of Service</a> apply.' +
										'</p>'
								);
							} else if (domain && domain.reCaptchaKey) {
								formElem.append(
									'<div class="g-recaptcha" data-sitekey="' + domain.reCaptchaKey + '"></div>'
								);
								formElem.append('<br>');
							} else {
								formElem.append('<div class="g-recaptcha" data-sitekey="{{RECAPTCHA_SITE}}"></div>');
								formElem.append('<br>');
							}
						}
					} else if (reCaptcha) {
						if (
							hasSoftDeployAccess('FORM_CAPTCHA_V3') &&
							(!domain || APPROVED_V3_CUSTOM_DOMAINS.includes(domain.name))
						) {
							// v3 is invisible — the editor mirrors the published form by showing the
							// required disclosure line instead of the (no-longer-rendered) v2 checkbox.
							var v3Note = jQuery('<p />');
							v3Note
								.css({ 'font-size': '11px', color: '#888', 'margin-top': '10px' })
								.addClass('recaptcha');
							v3Note.html(
								'This site is protected by reCAPTCHA and the Google ' +
									'<a href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a> and ' +
									'<a href="https://policies.google.com/terms" target="_blank">Terms of Service</a> apply.'
							);
							formElem.append(v3Note);
						} else {
							var fakeReCaptcha = jQuery('<div />');
							var fakeReCaptchaImg = jQuery('<img src="img/reCAPTCHA.png"/>');
							fakeReCaptchaImg.css('max-width', '100%');
							fakeReCaptchaImg.css('border-radius', '4px');
							fakeReCaptchaImg.css('background-color', 'white');
							fakeReCaptcha
								.css({
									display: 'block',
									'border-radius': '3px',
									'background-color': '#E4EAF0',
									'margin-bottom': '10px',
									'text-align': 'center'
								})
								.addClass('recaptcha');
							fakeReCaptcha.append(fakeReCaptchaImg);
							formElem.append(fakeReCaptcha);
						}
					}

					if (doubleOptIn) {
						var fakeOptin = jQuery('<div />');
						fakeOptin.css({
							display: 'table',
							width: '100%',
							'border-radius': '3px',
							'margin-bottom': '10px',
							'background-color': '#E4EAF0',
							'text-align': 'center',
							padding: '5px',
							'margin-top': '15px'
						});

						var fakeCheckboxWrapper = jQuery('<div />');
						var fakeCheckbox = jQuery('<input type="checkbox" />');
						fakeCheckbox.attr({
							name: 'Prefs.doubleOptIn'
						});

						fakeCheckboxWrapper.css({
							display: 'table-cell',
							'vertical-align': 'middle'
						});

						fakeCheckbox.css({
							'margin-left': '8px'
						});

						var fakeText = jQuery('<span>' + Parser.translate(doubleOptIn.title) + '</span>');
						fakeText.css({
							float: 'right',
							'text-align': 'left',
							width: 'calc(100% - 10px)',
							display: 'table-cell',
							'vertical-align': 'middle'
						});

						if (doubleOptIn.required) {
							fakeCheckbox.attr({
								required: true
							});

							fakeText.html(fakeText.html() + ' <span class="text-danger">*</span>');
						}

						fakeCheckboxWrapper.append(fakeCheckbox);
						fakeOptin.append(fakeCheckboxWrapper);
						fakeOptin.append(fakeText);
						fakeOptin.append('<br clear="all" />');
						formElem.append(fakeOptin);
					}

					var submitBtn = jQuery('<button type="submit"/>');

					if (!opts.embedded) {
						submitBtn.addClass('btn btn-primary btn-block btn-lg');
						submitBtn.css('border', 0);
					}
					submitBtn.text(translateLiveTags(form.buttonText, opts.liveTags));
					formElem.append(jQuery('<div />', { class: 'up-form-submit-row submit-button' }).append(submitBtn));

					if (opts.embedded) {
						formElem.append('\n');
					}

					wrapper.append(formElem);

					if (!opts.embedded) {
						wrapper.append(thanksElem);
						wrapper.append(thanksElemOnDemand);
						wrapper.append(eventFullElem);
						wrapper.append(eventCanceledElem);
						wrapper.append(eventPassedElem);
					}

					elem.append(wrapper);
				})();
				break;
			case 'map':
				if (config.value.address) {
					var address = translateLiveTags(config.value.address, opts.liveTags);
					var addressTranslated = opts.locationData && address === opts.locationData.address;
					elem.attr({
						'data-lat': addressTranslated ? opts.locationData.lat : config.value.lat,
						'data-lng': addressTranslated ? opts.locationData.lng : config.value.lng,
						'data-showtools': config.value.showTools,
						'data-allowscrollzoom': isDist ? config.value.allowScrollZoom : false
					});

					var map = jQuery('<div />');
					map.addClass('map');
					elem.append(map);

					if (config.value.showAddress) {
						var infoBox = jQuery('<div />');
						infoBox.addClass('map-info');
						infoBox.html(address);
						elem.prepend(infoBox);
					}

					if (!isDist) {
						var mapCover = jQuery('<div />');
						mapCover.css({
							position: 'absolute',
							top: 0,
							right: 0,
							bottom: 0,
							left: 0,
							backgroundColor: 'transparent'
						});
						elem.append(mapCover);
					} else {
						// Append fallback static map image
						var img = jQuery('<img />');
						var KEY = getGmapKey(domain);
						img.attr({
							src:
								'https://maps.googleapis.com/maps/api/staticmap?zoom=16&size=430x200&maptype=terrain&markers=size:tiny|color:red|' +
								address +
								'&key=' +
								KEY
						});
						map.append(img);
					}
				} else if (!isDist) {
					placeholder = getPlaceholder(Parser.translate('landingpage.enterAddressForMap'), null, 'fa fa-map');
				}
				break;

			case 'card':
				if (config.value.imgSrc) {
					var cardImg = jQuery('<div class="card-img" />');
					cardImg.css('background-image', "url('" + config.value.imgSrc + "')");

					if (config.value.imgOpacity !== undefined) {
						var pOpacity = parseInt(config.value.imgOpacity);
						if (!isNaN(pOpacity)) {
							cardImg.css('opacity', pOpacity / 100);
						}
					}

					if (config.value.imgSize) {
						cardImg.css('background-size', config.value.imgSize);
					}

					elem.append(cardImg);
				}

				elem.css('background-color', config.value.backgroundColor);

				var cardTitle = jQuery('<h3 class="card-title"/>');
				cardTitle.text(config.value.title);
				cardTitle.css('color', config.value.titleColor);

				var subtitle = jQuery('<h3 class="card-subtitle"/>');
				subtitle.text(config.value.subtitle);
				subtitle.css('color', config.value.textColor);

				var text = jQuery('<p class="card-text"/>');
				text.text(config.value.text);
				text.css('color', config.value.textColor);

				elem.append(cardTitle, subtitle, text);
				break;
			case 'links':
				elem.html('');
				_.each(config.value.options, function (op) {
					var li = jQuery('<li />');
					var a = jQuery('<a />');
					a.text(op.title);
					a.attr('href', op.href);
					li.append(a);
					elem.append(li);
				});
				break;
			case 'share':
				setElemText(elem, translateLiveTags(config.value.text, opts.liveTags));

				var buttons = jQuery('<div class="share-social-media-platforms" />');

				buttons.css({
					'font-size': config.value.size || '24px'
				});

				elem.css({
					color: config.value.buttonTextColor,
					'background-color': config.value.buttonBackgroundColor,
					padding: config.value.paddingV + 'px ' + config.value.paddingH + 'px',
					'font-size': config.value.fontSize + 'px'
				});

				elem.attr({
					href: config.value.link,
					target: config.value.srcTarget
				});

				if (config.value.align === 'center') {
					elem.removeClass('pull-left');
					elem.removeClass('pull-right');

					elem.css({
						'margin-left': 'auto',
						'margin-right': 'auto',
						display: 'table'
					});

					buttons.removeClass('pull-left');
					buttons.removeClass('pull-right');
					buttons.css({
						'margin-left': 'auto',
						'margin-right': 'auto',
						display: 'table'
					});
				} else if (config.value.align === 'right') {
					elem.removeClass('pull-left');
					elem.addClass('pull-right');
					buttons.removeClass('pull-left');
					buttons.addClass('pull-right');
				} else {
					elem.removeClass('pull-right');
					elem.addClass('pull-left');
					buttons.removeClass('pull-right');
					buttons.addClass('pull-left');
				}

				if (config.value.fullWidth) {
					elem.addClass('btn-block');
					buttons.addClass('btn-block');
				} else {
					elem.removeClass('btn-block');
					buttons.removeClass('btn-block');
				}

				var activePlatforms = _.reduce(
					config.value,
					function (out, value, platform) {
						if (value.active) {
							out.push(Object.assign({}, value, { name: platform }));
						}
						return out;
					},
					[]
				);

				var shareUrl = encodeURIComponent(
					form.domain
						? 'http://' + form.domain + '/' + form.urlName
						: appConfig.LANDINGPAGEPATH + '/' + (form.uuid || '{{UUID}}')
				);
				var hrefMap = {
					facebook:
						'https://www.facebook.com/sharer/sharer.php?kid_directed_site=0&u=' +
						shareUrl +
						'&display=popup&ref=plugin&src=share_button',
					linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + shareUrl,
					twitter:
						'https://twitter.com/intent/tweet?text=' +
						encodeURIComponent(form.name) +
						'&tw_p=tweetbutton&url=' +
						shareUrl
				};

				for (var i = 0; i < activePlatforms.length; i++) {
					var href = hrefMap[activePlatforms[i].name];
					var platformElement = jQuery('<a href="' + href + '" target="_blank" />');
					platformElement.addClass('up-unstyled-element');
					platformElement.append(
						jQuery(
							'<b style="color: ' +
								activePlatforms[i].color +
								'" class="fa fa-' +
								activePlatforms[i].name +
								'-square" />'
						)
					);

					buttons.append(platformElement);
					if (i === 0) {
						platformElement.css({
							'margin-right': '5px'
						});
					} else if (i === activePlatforms.length) {
						platformElement.css({
							'margin-left': '5px'
						});
					} else {
						platformElement.css({
							margin: '0 5px'
						});
					}
				}

				elem.append(buttons);

				break;
			case 'html':
				var customHtml = config.value.html;
				if (customHtml) {
					// Inject the raw markup verbatim. We use native innerHTML rather
					// than jQuery's .html() so that any <script> tags are preserved in
					// the serialized published page but not executed in the live editor
					// preview.
					elem[0].innerHTML = customHtml;
				} else if (!isDist) {
					placeholder = getPlaceholder(Parser.translate('landingpage.enterHtmlHere'), null, 'fa fa-code');
				}
				break;
		}

		if (config.style) {
			elem.css(config.style);
		}

		return placeholder;
	};

	var parseRow = function (row, config) {
		var container = row.find(selectors.CONTAINER);

		if (!config.visible) {
			container.css('display', 'none');
			row.css('z-index', -1);
		}

		if (config.background.fullWidth) {
			if (!config.transparentHeader && config.visible) {
				row.css('background-color', config.background.color);
			}
		} else {
			if (!config.transparentHeader && config.visible) {
				container.css('background-color', config.background.color);
			}
		}

		// Look for pior special elements
		var inheritedBg = row.find('[data-inherit-bg]');
		if (inheritedBg.length) {
			inheritedBg.css('background-color', config.background.color);
		}

		if (config.background.img && !config.transparentHeader) {
			var imgPath = config.background.img;
			var bgElem = jQuery('<div></div>');
			bgElem.addClass('up-background-image');
			bgElem.css('background-image', "url('" + imgPath + "')");
			if (config.background.imgOpacity !== undefined) {
				var parsed = parseInt(config.background.imgOpacity);
				if (!isNaN(parsed)) {
					var opacity = parsed / 100;
					bgElem.css('opacity', opacity);
				}
			}
			if (config.background.imgBlur) {
				bgElem.css('filter', 'blur(' + config.background.imgBlur + 'px)');
			}
			if (config.background.imgSize) {
				bgElem.css('background-size', config.background.imgSize);
			}
			if (config.background.imgRepeat) {
				bgElem.css('background-repeat', config.background.imgRepeat);
			}
			if (config.background.fullWidth) {
				row.prepend(bgElem);
			} else {
				container.prepend(bgElem);
			}
		}

		// Remove meta element
		var bgElement = row.find(selectors.BACKGROUND);
		if (bgElement.length) {
			bgElement.remove();
		}
	};

	var getProfileProp = function (p, path, def, unit, multiply) {
		if (!p) {
			return def;
		}
		unit = unit || '';
		var splitted = path.split('.');
		var res = p;
		splitted.forEach(function (key) {
			if (!res[key]) {
				res = def;
				return;
			} else {
				res = res[key];
			}
		});

		if (typeof res === 'boolean') {
			return res;
		}

		if (res && multiply) {
			return res * multiply + unit;
		}

		return (res || def) + unit;
	};

	var getSelector = function (isDist, selector) {
		return isDist ? selector : '.preview ' + selector;
	};

	var getStyle = function (p, onlyContent, isDist, isPreview) {
		var path = p.typographyLandingPages ? 'typographyLandingPages' : 'typography';

		// Built in chunks on purpose: as one 562-deep `+` chain, rspack 2.2's React Compiler overflows its
		// stack and kills the whole build with SIGILL (fine at 150 terms, dies at 200; rspack#15934).
		let style =
			(onlyContent ? '' : '<style>') +
			getSelector(isDist, '.template-scoped-style') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'p.fontFamily', 'inherit') +
			';' +
			'padding: 0 15px;' +
			'}' +
			getSelector(isDist, '.template-scoped-style.frame') +
			'{' +
			'padding: 0;' +
			'}' +
			getSelector(isDist, '.template-scoped-style.frame #up-form-element-wrapper') +
			'{' +
			'margin: 0 !important;' +
			'width: initial !important;' +
			'}' +
			getSelector(isDist, '.template-scoped-style.frame #up-form') +
			'{' +
			'box-shadow: none;' +
			'border-radius: 0;' +
			'background-color: transparent;' +
			'}' +
			getSelector(isDist, 'p') +
			',' +
			getSelector(isDist, 'label') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'p.fontFamily', 'inherit') +
			';' +
			'font-size: ' +
			getProfileProp(p, path + '.' + 'p.size', 'inherit', 'px') +
			';' +
			'font-weight: ' +
			(getProfileProp(p, path + '.' + 'p.bold', false) ? 'bold' : 'inherit') +
			';' +
			'font-style: ' +
			(getProfileProp(p, path + '.' + 'p.italic', false) ? 'italic' : 'inherit') +
			';' +
			'text-decoration: ' +
			(getProfileProp(p, path + '.' + 'p.underline', false) ? 'underline' : 'inherit') +
			';' +
			'color: inherit;' +
			'}' +
			getSelector(isDist, 'p.ingress') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'ingress.fontFamily', 'inherit') +
			';' +
			'font-size: ';
		style =
			style +
			getProfileProp(p, path + '.' + 'ingress.size', 'inherit', 'px') +
			';' +
			'font-weight: ' +
			(getProfileProp(p, path + '.' + 'ingress.bold', false) ? 'bold' : 'inherit') +
			';' +
			'font-style: ' +
			(getProfileProp(p, path + '.' + 'ingress.italic', false) ? 'italic' : 'inherit') +
			';' +
			'text-decoration: ' +
			(getProfileProp(p, path + '.' + 'ingress.underline', false) ? 'underline' : 'inherit') +
			';' +
			'}' +
			getSelector(isDist, 'h1') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'h2.fontFamily', 'inherit') +
			';' +
			'display: block;' +
			'}' +
			getSelector(isDist, 'h2') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'h2.fontFamily', 'inherit') +
			';' +
			'font-size: ' +
			getProfileProp(p, path + '.' + 'h2.size', 'inherit', 'px') +
			';' +
			'font-weight: ' +
			(getProfileProp(p, path + '.' + 'h2.bold', false) ? 'bold' : 'inherit') +
			';' +
			'font-style: ' +
			(getProfileProp(p, path + '.' + 'h2.italic', false) ? 'italic' : 'inherit') +
			';' +
			'text-decoration: ' +
			(getProfileProp(p, path + '.' + 'h2.underline', false) ? 'underline' : 'inherit') +
			';' +
			'display: block;' +
			'margin: 0;' +
			'}' +
			getSelector(isDist, 'h3') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'h3.fontFamily', 'inherit') +
			';' +
			'font-size: ' +
			getProfileProp(p, path + '.' + 'h3.size', 'inherit', 'px') +
			';' +
			'font-weight: ' +
			(getProfileProp(p, path + '.' + 'h3.bold', false) ? 'bold' : 'inherit') +
			';';
		style =
			style +
			'font-style: ' +
			(getProfileProp(p, path + '.' + 'h3.italic', false) ? 'italic' : 'inherit') +
			';' +
			'text-decoration: ' +
			(getProfileProp(p, path + '.' + 'h3.underline', false) ? 'underline' : 'inherit') +
			';' +
			'display: block;' +
			'margin: 0;' +
			'}' +
			getSelector(isDist, 'h2.large') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'h2.fontFamily', 'inherit') +
			';' +
			'font-size: ' +
			getProfileProp(p, path + '.' + 'h2.size', 'inherit', 'px', 2) +
			';' +
			'}' +
			getSelector(isDist, 'h3.large') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'h3.fontFamily', 'inherit') +
			';' +
			'font-size: ' +
			getProfileProp(p, path + '.' + 'h3.size', 'inherit', 'px', 2) +
			';' +
			'}' +
			getSelector(isDist, 'a:not(.up-unstyled-element)') +
			'{' +
			'font-family: ' +
			getProfileProp(p, path + '.' + 'link.fontFamily', 'inherit') +
			';' +
			'font-size: inherit' +
			';' +
			'font-weight: ' +
			(getProfileProp(p, path + '.' + 'link.bold', false) ? 'bold' : 'inherit') +
			';' +
			'font-style: ' +
			(getProfileProp(p, path + '.' + 'link.italic', false) ? 'italic' : 'inherit') +
			';' +
			'text-decoration: ' +
			(getProfileProp(p, path + '.' + 'link.underline', false) ? 'underline' : 'inherit') +
			';' +
			'}' +
			getSelector(isDist, '.up-row') +
			'{' +
			'position: relative;' +
			'}' +
			getSelector(isDist, '.up-row.up-absolute-row') +
			'{';
		style =
			style +
			'position: absolute !important;' +
			'left: 0;' +
			'z-index: 1004;' +
			'right: 0;' +
			'background-color: transparent;' +
			'pointer-events: none;' +
			'}' +
			getSelector(isDist, '.up-row.up-absolute-row *') +
			'{' +
			'pointer-events: auto;' +
			'}' +
			getSelector(isDist, '.up-container') +
			'{' +
			'position: relative;' +
			'z-index: 1003;' +
			'word-break: break-word;' +
			'}' +
			getSelector(isDist, '.twitter-typeahead') +
			'{' +
			'display: block !important;' +
			'}' +
			getSelector(isDist, '.up-background-image') +
			'{' +
			'position: absolute;' +
			'top: 0;' +
			'bottom: 0;' +
			'left: 0;' +
			'right: 0;' +
			'background-position: center center;' +
			'background-size: cover;' +
			'}' +
			getSelector(isDist, '.up-form-wrapper button[type=submit]') +
			'{' +
			'height: 45px;' +
			'margin-top: 20px;' +
			'}' +
			getSelector(isDist, '.up-form-wrapper h2') +
			'{' +
			'word-break: break-word;' +
			'margin-bottom: 10px;' +
			'}' +
			getSelector(isDist, '.up-form-wrapper-cover') +
			'{' +
			'position: absolute;' +
			'top: 0;' +
			'bottom: 0;' +
			'left: 0;' +
			'right: 0;' +
			'}' +
			getSelector(isDist, '.form-group');
		style =
			style +
			'{' +
			'position: relative;' +
			'}' +
			getSelector(isDist, '.error-message') +
			'{' +
			'background-color: #dc0d17;' +
			'padding: 1px 5px;' +
			'color: white;' +
			'font-style: italic;' +
			'}' +
			getSelector(isDist, '.top-carot') +
			'{' +
			'position: absolute;' +
			'width: 0;' +
			'height: 0;' +
			'margin-top: -7px;' +
			'margin-left: 10px;' +
			'border-left: 7px solid transparent;' +
			'border-right: 7px solid transparent;' +
			'border-bottom: 7px solid #dc0d17;' +
			'}' +
			getSelector(isDist, '.error-body') +
			'{' +
			'margin: 2px 5px;' +
			'font-size: 12px;' +
			'color: white !important;' +
			'}' +
			getSelector(isDist, 'b#up-email-warning') +
			'{' +
			'position: absolute;' +
			'top: 33px;' +
			'right: 15px;' +
			'color: #dc0d17;' +
			'font-style: normal;' +
			'}' +
			getSelector(isDist, '#up-client-spinner') +
			'{' +
			'position: absolute;' +
			'display: none;' +
			'bottom: 10px;' +
			'right: 10px;' +
			'color: #6B7C93;' +
			'}' +
			getSelector(isDist, '.up-form-wrapper') +
			'{' +
			'position: relative;' +
			'}' +
			getSelector(isDist, '#up-waiting-list-btn') +
			'{' +
			'display: none;';
		style =
			style +
			'margin-top: 10px;' +
			'}' +
			getSelector(isDist, '#up-form-thanks') +
			',' +
			getSelector(isDist, '#up-form-thanks-on-demand') +
			',' +
			getSelector(isDist, '#up-event-canceled') +
			',' +
			getSelector(isDist, '#up-form-event-full') +
			',' +
			getSelector(isDist, '#up-event-passed') +
			',' +
			getSelector(isDist, '#up-form') +
			'{' +
			'display: none;' +
			'background-color: #fff;' +
			'padding: 20px;' +
			'border-radius: 3px;' +
			'box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);' +
			'}' +
			(isPreview ? getSelector(isDist, '#up-form') + '{' + 'display: block;' + '}' : '') +
			getSelector(isDist, '.up-element #up-form-thanks .up-editor-placeholder') +
			',' +
			getSelector(isDist, '.up-element #up-form-thanks-on-demand .up-editor-placeholder') +
			'{' +
			'margin: -20px;' +
			'margin-top: -40px;' +
			'}' +
			getSelector(isDist, '.up-element.has-placeholder') +
			'{' +
			'padding: 0 !important;' +
			'background-color: transparent !important;' +
			'}' +
			getSelector(isDist, '.up-element.type-button h2') +
			'{' +
			'display: block;' +
			'text-align: center;' +
			'}' +
			getSelector(isDist, '.up-element.type-file') +
			'{' +
			'text-align: center;' +
			'}' +
			getSelector(isDist, '.up-element.type-file h2') +
			'{' +
			'display: block;' +
			'}' +
			getSelector(isDist, '.up-element.type-file img') +
			'{' +
			'max-width: 100%;' +
			'}';
		style =
			style +
			getSelector(isDist, '.up-element.type-file a.up-file-btn') +
			'{' +
			'padding: 10px 20px;' +
			'border-radius: 3px;' +
			'border: none;' +
			'font-size: 14px;' +
			'text-align: center;' +
			'display: inline-block;' +
			'margin: 10px 0;' +
			'color: #ffffff;' +
			'background-color: #002f65;' +
			'text-decoration: none;' +
			'}' +
			getSelector(isDist, '.up-element.type-file a:hover') +
			',' +
			getSelector(isDist, '.up-element.type-file a:focus') +
			',' +
			getSelector(isDist, '.up-element.type-file a:active') +
			'{' +
			'text-decoration: none;' +
			'}' +
			getSelector(isDist, '.up-element.type-map .map-info') +
			'{' +
			'position: absolute;' +
			'top: 0;' +
			'left: 0;' +
			'right: 0;' +
			'padding: 10px;' +
			'font-size: 14px;' +
			'background-color: #ffffff;' +
			'color: #000000;' +
			'border-bottom: 1px solid #A4B3C7;' +
			'text-align: center;' +
			'height: 40px;' +
			'z-index: 1000;' +
			'}' +
			getSelector(isDist, '.up-element.type-map .map-info + .map') +
			'{' +
			'margin-top: 40px;' +
			'}' +
			getSelector(isDist, '.up-element.type-map') +
			'{' +
			'border-radius: 3px;' +
			'overflow: hidden;' +
			'position: relative;' +
			'}' +
			getSelector(isDist, '.up-element.type-map .map') +
			'{' +
			'min-height: 200px;' +
			'}';
		style =
			style +
			getSelector(isDist, '.up-element.type-card') +
			'{' +
			'background-color: #fff;' +
			'box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);' +
			'padding: 10px 30px;' +
			'border-radius: 5px;' +
			'position: relative;' +
			'overflow: hidden;' +
			'text-align: center;' +
			'max-width: 300px;' +
			'margin: 0 auto;' +
			'}' +
			getSelector(isDist, '.up-element.type-card .card-img') +
			'{' +
			'height: 200px;' +
			'background-position: center center;' +
			'background-repeat: no-repeat;' +
			'background-size: cover;' +
			'position: absolute;' +
			'top: 0;' +
			'left: 0;' +
			'right: 0;' +
			'}' +
			getSelector(isDist, '.up-element.type-card .card-title') +
			'{' +
			'font-size: 18px;' +
			'line-height: 18px;' +
			'margin-top: 10px;' +
			'margin-bottom: 10px;' +
			'font-weight: bold;' +
			'}' +
			getSelector(isDist, '.up-element.type-card .card-text') +
			'{' +
			'font-size: 12px;' +
			'margin: 10px 0;' +
			'line-height: 16px;' +
			'}' +
			getSelector(isDist, '.up-element.type-card .card-subtitle') +
			'{' +
			'margin: 10px 0;' +
			'font-size: 12px;' +
			'line-height: 12px;' +
			'font-weight: normal;' +
			'}' +
			getSelector(isDist, '.up-element.type-card .card-img + .card-title') +
			'{' +
			'margin-top: 210px;' +
			'}' +
			getSelector(isDist, '.up-element.type-countdown') +
			'{';
		style =
			style +
			'text-align: center;' +
			'background-color: #fff;' +
			'padding: 15px;' +
			'border-radius: 3px;' +
			'}' +
			getSelector(isDist, '.up-element.type-countdown .cd-unit') +
			'{' +
			'display: inline-block;' +
			'width: 20%;' +
			'min-width: 100px;' +
			'}' +
			getSelector(isDist, '.up-element.type-countdown .cd-unit .cd-header') +
			'{' +
			'text-transform: uppercase;' +
			'color: #6B7C93;' +
			'font-size: 14px;' +
			'}' +
			getSelector(isDist, '.up-element.type-countdown .cd-unit h2') +
			'{' +
			'font-size: 42px;' +
			'}' +
			getSelector(isDist, '.up-element.type-video iframe') +
			'{' +
			'display: block;' +
			'}' +
			getSelector(isDist, '.up-element.type-video.shadow') +
			'{' +
			'box-shadow: 0 2px 10px rgba(0,0,0, 0.5)' +
			'}' +
			getSelector(isDist, '.up-element.type-video.vimeo-video') +
			'{' +
			'width: 100%;' +
			'}' +
			getSelector(isDist, '.up-element.type-image') +
			'{' +
			'max-width: 100%;' +
			'}' +
			getSelector(isDist, '.fade-animation') +
			'{' +
			'opacity: 0;' +
			'animation-fill-mode: forwards;' +
			'animation-timing-function: ease-in-out;' +
			'}' +
			getSelector(isDist, '#up-click-cover') +
			'{' +
			'position: fixed;' +
			'z-index: 9999;' +
			'top: 0;' +
			'bottom: 0;' +
			'left: 0;';
		style =
			style +
			'right: 0;' +
			'background-color: transparent;' +
			'cursor: pointer;' +
			'}' +
			getSelector(isDist, '.opt-in-label') +
			'{margin-bottom: 0;}' +
			getSelector(isDist, '.vpadding-none') +
			'{padding-top: 0px !important; padding-bottom: 0px !important;}' +
			getSelector(isDist, '.hpadding-none') +
			'{padding-left: 0px !important; padding-right: 0px !important;}' +
			getSelector(isDist, '.vpadding-xs') +
			'{padding-top: 10px !important; padding-bottom: 10px !important;}' +
			getSelector(isDist, '.hpadding-xs') +
			'{padding-left: 10px !important; padding-right: 10px !important;}' +
			getSelector(isDist, '.vpadding-sm') +
			'{padding-top: 20px !important; padding-bottom: 20px !important;}' +
			getSelector(isDist, '.hpadding-sm') +
			'{padding-left: 20px !important; padding-right: 20px !important;}' +
			getSelector(isDist, '.vpadding-md') +
			'{padding-top: 40px !important; padding-bottom: 40px !important;}' +
			getSelector(isDist, '.hpadding-md') +
			'{padding-left: 40px !important; padding-right: 40px !important;}' +
			getSelector(isDist, '.vpadding-lg') +
			'{padding-top: 60px !important; padding-bottom: 60px !important;}' +
			getSelector(isDist, '.hpadding-lg') +
			'{padding-left: 60px !important; padding-right: 60px !important;}' +
			getSelector(isDist, '.vpadding-xl') +
			'{padding-top: 100px !important; padding-bottom: 100px !important;}' +
			getSelector(isDist, '.hpadding-xl') +
			'{padding-left: 100px !important; padding-right: 100px !important;}' +
			getSelector(isDist, '.vpadding-xxl') +
			'{padding-top: 160px !important; padding-bottom: 160px !important;}' +
			getSelector(isDist, '.hpadding-xxl') +
			'{padding-left: 160px !important; padding-right: 160px !important;}' +
			'@media (max-width: 992px) {' +
			getSelector(isDist, '.vpadding-xs') +
			'{padding-top: 0px !important; padding-bottom: 0px !important;}' +
			getSelector(isDist, '.hpadding-xs') +
			'{padding-left: 0px !important; padding-right: 0px !important;}' +
			getSelector(isDist, '.vpadding-xs') +
			'{padding-top: 5px !important; padding-bottom: 5px !important;}' +
			getSelector(isDist, '.hpadding-xs') +
			'{padding-left: 5px !important; padding-right: 5px !important;}' +
			getSelector(isDist, '.vpadding-sm') +
			'{padding-top: 10px !important; padding-bottom: 10px !important;}' +
			getSelector(isDist, '.hpadding-sm') +
			'{padding-left: 10px !important; padding-right: 10px !important;}' +
			getSelector(isDist, '.vpadding-md') +
			'{padding-top: 20px !important; padding-bottom: 20px !important;}' +
			getSelector(isDist, '.hpadding-md');
		style =
			style +
			'{padding-left: 20px !important; padding-right: 20px !important;}' +
			getSelector(isDist, '.vpadding-lg') +
			'{padding-top: 30px !important; padding-bottom: 30px !important;}' +
			getSelector(isDist, '.hpadding-lg') +
			'{padding-left: 30px !important; padding-right: 30px !important;}' +
			getSelector(isDist, '.vpadding-xl') +
			'{padding-top: 50px !important; padding-bottom: 50px !important;}' +
			getSelector(isDist, '.hpadding-xl') +
			'{padding-left: 50px !important; padding-right: 50px !important;}' +
			getSelector(isDist, '.vpadding-xxl') +
			'{padding-top: 80px !important; padding-bottom: 80px !important;}' +
			getSelector(isDist, '.hpadding-xxl') +
			'{padding-left: 80px !important; padding-right: 80px !important;}' +
			'}' +
			'.up-btn, .editor-placeholder-text, .preview h3.editor-placeholder-body {' +
			'	font-family: "Roboto", sans-serif;' +
			'}' +
			'.iti.iti--allow-dropdown {' +
			'	display: block;' +
			'}' +
			'.iti__flag-box.iti__flag-box {' +
			'	display: none;' +
			'}' +
			'@keyframes fadeIn {' +
			'from {' +
			'opacity: 0;' +
			'}' +
			'to {' +
			'opacity: 1;' +
			'}' +
			'}' +
			'@keyframes fadeInLeft {' +
			'from {' +
			'opacity: 0;' +
			'margin-left: -20px;' +
			'}' +
			'to {' +
			'opacity: 1;' +
			'margin-left: 0px;' +
			'}' +
			'}' +
			'@keyframes fadeInRight {' +
			'from {' +
			'opacity: 0;' +
			'margin-right: -20px;' +
			'}' +
			'to {' +
			'opacity: 1;' +
			'margin-right: 0px;' +
			'}';
		style =
			style +
			'}' +
			'@keyframes zoomIn {' +
			'from {' +
			'opacity: 0;' +
			'transform: scale(0);' +
			'}' +
			'to {' +
			'opacity: 1;' +
			'margin-right: scale(1);' +
			'}' +
			'}' +
			`.spinner {
				animation: rotate 2s linear infinite;
				width: 18px;
				height: 18px;
			}
			.spinner .path {
				stroke: black;
				stroke-linecap: round;
				animation: dash 1.5s ease-in-out infinite;
			}
			@keyframes rotate {
				100% {
					transform: rotate(360deg);
				}
			}
			@keyframes dash {
				0% {
					stroke-dasharray: 1, 150;
					stroke-dashoffset: 0;
				}
				50% {
					stroke-dasharray: 90, 150;
					stroke-dashoffset: -35;
				}
				100% {
					stroke-dasharray: 90, 150;
					stroke-dashoffset: -124;
				}
			}` +
			(onlyContent ? '' : '</style>');
		return style;
	};

	var getBootstrap = function () {
		var bootstrap =
			'<link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css" integrity="sha384-BVYiiSIFeK1dGmJRAkycuHAHRg32OmUcww7on3RYdg4Va+PmSTsz/K68vbdEjh4u" crossorigin="anonymous">';
		return bootstrap;
	};

	var getCustomFonts = function (fonts) {
		if (fonts) {
			return fonts
				.map(function (font) {
					return (
						'<link href="https://fonts.googleapis.com/css?family=' +
						encodeURI(font.family) +
						':wght@400,700&display=swap" rel="stylesheet" />'
					);
				})
				.join('');
		}

		return '';
	};

	function getIntlTelLibrary() {
		// Self-host these on same origin. cdnjs.cloudflare.com is on EasyPrivacy and similar
		// lists, which broke event-form submissions for prospects using stricter ad blockers.
		// Every /static/ url in this file must also be allow-listed in
		// api/src/controller/eventPageStatic.ts, or it 404s on a customer's own domain.
		return (
			'<link rel="stylesheet" href="/static/intlTelInput.min.css">' +
			'<script src="/static/intlTelInput.min.js"></script>'
		);
	}

	var getImageForSocialMediaTags = function (body, isLandingPage) {
		if (!body && isLandingPage) {
			body = this.props.form.landingPageBody;
		}

		if (isLandingPage) {
			for (var rowIndex = 0; rowIndex < body.rows.length; rowIndex++) {
				var row = body.rows[rowIndex];
				if (row.columns) {
					for (var columnIndex = 0; columnIndex < row.columns.length; columnIndex++) {
						var column = row.columns[columnIndex];
						for (var elementIndex = 0; elementIndex < column.elements.length; elementIndex++) {
							var element = column.elements[elementIndex];
							if (element.type === 'image' && element.value && element.value.src) {
								return element.value.src;
							}
						}
					}
				}

				if (row.background && row.background.img) {
					return row.background.img;
				}
			}

			return '';
		}
	};

	var getDescriptionForSocialMediaTags = function (html) {
		if (!html) {
			return '';
		}

		var cleaned = [];

		var matchesSocialEventDescription = html.match(
			/<(div|span)(.*)class="social-event-description"(.*)>(.*)<\/(div|span)>/g
		);
		var filteredMatchesSocialEvent = _.filter(matchesSocialEventDescription, function (item) {
			return item !== '<div>&nbsp;</div>';
		});

		_.each(filteredMatchesSocialEvent, function (row) {
			row = row.replace(new RegExp('<[^>]*>', 'g'), '').replace(/&nbsp;/g, ' ');
			cleaned.push(row);
		});

		var matchesH3 = html.match(/<h3(.*)>(.*)<\/h3>/g);
		var filteredMatchesH3 = _.filter(matchesH3, function (item) {
			return item !== '<h3>&nbsp;</h3>';
		});

		_.each(filteredMatchesH3, function (row) {
			row = row.replace(new RegExp('<[^>]*>', 'g'), '');
			cleaned.push(row);
		});

		var matches = html.match(/<p(.*)>(.*)<\/p>/g);
		var filteredMatches = _.filter(matches, function (item) {
			return item !== '<p>&nbsp;</p>';
		});

		_.each(filteredMatches, function (row) {
			row = row.replace(new RegExp('<[^>]*>', 'g'), '');
			cleaned.push(row);
		});

		return cleaned.length ? cleaned[0] : '';
	};

	var getTitleForSocialMediaTags = function (markdown) {
		if (!markdown) {
			return '';
		}

		var cleaned = [];
		var matches = markdown.match(/<h1(.*)>(.*)<\/h1>/g);
		_.each(matches, function (row) {
			row = row.replace(new RegExp('<[^>]*>', 'g'), '');
			cleaned.push(row);
		});

		var matches2 = markdown.match(/<h2(.*)>(.*)<\/h2>/g);
		_.each(matches2, function (row) {
			row = row.replace(new RegExp('<[^>]*>', 'g'), '');
			cleaned.push(row);
		});

		return cleaned[0];
	};

	var generateSocialMediaMetaTags = function (data) {
		var socialMediaArray = [];

		socialMediaArray.push('<meta property="og:title" content="' + escapeHtml(data.name) + '" />');
		socialMediaArray.push('<meta property="og:description" content="' + escapeHtml(data.desc) + '" />');

		socialMediaArray.push('<meta property="og:url" content="' + escapeHtml(data.url) + '" />');
		socialMediaArray.push('<meta property="og:image" content="' + encodeURI(data.img) + '" />');
		socialMediaArray.push('<meta property="og:image:width" content="' + escapeHtml(data.imgWidth) + '" />');
		socialMediaArray.push('<meta property="og:image:height" content="' + escapeHtml(data.imgHeight) + '" />');

		socialMediaArray.push('<meta name="twitter:card" content="summary">');
		socialMediaArray.push('<meta name="twitter:title" content="' + escapeHtml(data.name) + '">');
		socialMediaArray.push('<meta name="twitter:description" content="' + escapeHtml(data.desc) + '">');
		socialMediaArray.push('<meta name="twitter:image:src" content="' + encodeURI(data.img) + '">');

		return socialMediaArray;
	};

	var buildSocialMediaLinks = function (isDist, opts, form) {
		var domain;
		if (opts.domains && form.domain) {
			domain = _.find(opts.domains, { name: form.domain });
		}
		var h = '';

		if (
			form.socialMediaTags &&
			(form.socialMediaTags.hasOwnProperty('wasSetManually') === false || form.socialMediaTags.wasSetManually)
		) {
			_.each(form.socialMediaTags.tags, function (item) {
				h += item;
			});
		} else {
			var description = getDescriptionForSocialMediaTags(
				form.html || (form.landingPageBody && form.landingPageBody.markdown)
			);
			var image = form.landingPageBody && getImageForSocialMediaTags(form.landingPageBody, true);
			var customTitle = getTitleForSocialMediaTags(
				form.html || (form.landingPageBody && form.landingPageBody.markdown)
			);

			if (form.landingPageBody === null && !form.description.length) {
				// this is a form that doesnt have a description,
				// shouldnt get thank you page content as description.
				description = AppService.getAccountSelf().client.name;
			}

			var tagValues = {
				desc: description.substr(0, 147),
				img: image,
				imgHeight: 400,
				imgWidth: 400,
				name: (form.name || form.title || customTitle || '').substr(0, 87),
				url: domain
					? 'http://' + form.domain + '/' + form.urlName
					: appConfig.LANDINGPAGEPATH + '/' + (form.uuid || '{{UUID}}')
			};

			var socialMediaTags = generateSocialMediaMetaTags(tagValues);

			_.each(socialMediaTags, function (item) {
				h += item;
			});

			form.socialMediaTags = {
				tags: socialMediaTags,
				values: tagValues,
				wasSetManually: false
			};
		}

		return h;
	};

	function generateSocialMediaTagsObject(form, data, domain, urlName) {
		var description = getDescriptionForSocialMediaTags(
			form.html || (form.landingPageBody && form.landingPageBody.markdown)
		);

		var image;

		if (form.socialMediaTags?.wasSetManually) {
			image = form.socialMediaTags.values.img;
		} else {
			image = form.landingPageBody && getImageForSocialMediaTags(form.landingPageBody, true);
		}

		var tagValues = {
			desc: (description || '').substr(0, 147),
			img: image,
			imgHeight: 400,
			imgWidth: 400,
			name: (data.name || '').substr(0, 87),
			url: domain ? 'http://' + domain + '/' + urlName : appConfig.LANDINGPAGEPATH + '/' + (form.uuid || '{{UUID}}')
		};

		var socialMediaTags = generateSocialMediaMetaTags(tagValues);

		return {
			tags: socialMediaTags,
			values: tagValues,
			wasSetManually: form.socialMediaTags?.wasSetManually ?? false
		};
	}

	var getOpenTerms = function (opts) {
		var terms = _.reduce(
			opts.optIns,
			function (obj, opt) {
				obj[opt.id] = opt.terms;
				return obj;
			},
			{}
		);
		var script = 'function openTerms(id) {\n';
		script += 'var terms = ' + JSON.stringify(terms) + ';\n';
		script +=
			'var preview = window.open("", "", "height=700,width=800,scrollbars=yes,resizable=yes,toolbar=no,status=no,menu=no,titlebar=no,location=no,addressbar=no");\n';
		script +=
			'var html="<head><meta charset=\\"UTF-8\\"><style>body {margin: 0; padding: 20px;color: #333;font-family: Helvetica;font-size: 14px;}</style></head><body>" + terms[id] + "</body>";\n';
		script += 'preview.document.write(html);\n';
		script += '}\n';

		return script;
	};

	var getDefaultStyle = function (form) {
		var style = '';

		if (form.textColor) {
			style +=
				'#up-form h2, #up-form p, #up-form label, #up-form-thanks h2, #up-form-thanks-on-demand h2, #up-form-thanks p, #up-form-thanks-on-demand p {\n';
			style += '\tcolor: ' + form.textColor + ';\n';
			style += '}\n';
		}

		if (form.linkColor) {
			style += '#up-form a {\n';
			style += '\tcolor: ' + form.linkColor + ';\n';
			style += '}\n';
		}

		if (form.buttonBgColor || form.buttonTextColor) {
			style += '#up-form button {\n';

			if (form.buttonBgColor) {
				style += '\tbackground-color: ' + form.buttonBgColor + ';\n';
			}

			if (form.buttonTextColor) {
				style += '\tcolor: ' + form.buttonTextColor + ';\n';
			}

			style += '}\n';
		}

		if (form.backgroundColor) {
			style += '#up-form, #up-form-thanks, #up-form-thanks-on-demand {\n';
			style += '\tbackground-color: ' + form.backgroundColor + ' !important;\n';

			if (form.backgroundColor === 'transparent') {
				style += '\tbox-shadow: none !important;\n';
			}

			style += '}\n';
		}

		return style ? '<style>\n/* DEFAULT STYLE */\n' + style + '</style>' : '';
	};

	var getCustomStyle = function (form) {
		var styleField = form.fields.find(function (field) {
			return field.name.indexOf('Prefs.css') === 0;
		});

		if (!styleField) return '';

		var parser = new root.cssjs.cssjs();
		var parsed = parser.parseCSS(styleField.options);

		var style = parsed
			.map(function (styles) {
				if (styles.type === 'media') {
					const mediaBody = styles.subStyles
						.map(function (innerStyles) {
							return getStyleBody(innerStyles, true);
						})
						.join('');
					return `${styles.selector}` + '{\n' + '\n' + mediaBody + '\n}';
				} else {
					return getStyleBody(styles, false);
				}
			})
			.join('');

		return style ? '<style>\n/* CUSTOM STYLE */\n' + style + '</style>' : '';
	};

	/**
	 * Generate form class body
	 * @returns {string} css class body
	 */
	function getStyleBody(styles, isMedia) {
		var selectors = styles.selector.split(',').map(function (styleClass) {
			return isMedia
				? `#up-form ${styleClass}`
				: '#up-form ' + styleClass.replace(/(#up-form )|(^#up-form$)/g, '').trim();
		});

		var props = (styles.rules || [])
			.map(function (rule) {
				return '\t' + rule.directive + ': ' + rule.value + ';\n';
			})
			.join('');

		return selectors.join().trim() + ' {\n' + props + '}\n';
	}

	function getTelInputInitScript() {
		var utilsScriptUrl = '/static/intl-tel-input-utils.min.js';
		var code =
			'' +
			'    (function() {\n' +
			"      var phoneFields = document.querySelectorAll('input[name*=phone], input[name*=Phone]');\n" +
			'      [].forEach.call(phoneFields, function(element) {\n' +
			'        window.hasPhoneFields = true;\n' +
			'        element.iti = window.intlTelInput(element, {\n' +
			'          preferredCountries: [],\n' +
			"          initialCountry: 'auto',\n" +
			"          utilsScript: '" +
			utilsScriptUrl +
			"',\n" +
			'          geoIpLookup: function(success) {\n' +
			'            var xhr = new XMLHttpRequest();\n' +
			"            xhr.open('GET', '{{EXTERNAL_PATH}}whereAmI');\n" +
			'            xhr.onload = function() {\n' +
			'              success(xhr.status === 200 && !xhr.responseText?.includes("no-js") ? (JSON.parse(xhr.responseText).data || "") : "");\n' +
			'            };\n' +
			'            xhr.send();\n' +
			'          }\n' +
			'        });\n' +
			'      });\n' +
			'    })();';

		return code;
	}

	function getSubmitScript() {
		var script = '<script>\n';
		script += '(function(){';
		script += 'var form = document.getElementById("up-form");';
		script += 'if(form) {';
		script += 'form.addEventListener("submit", function(ev) {';
		script += 'var button = ev.target.querySelector("button[type=submit]");';
		script += 'if(button) {';
		script += 'button.disabled = true;';
		script += '}';
		script += '});';
		script += '}';
		script += '})();\n';
		script += '</script>';

		return script;
	}

	function getFileUploadScript(form) {
		var hasFileField = (form.fields || []).some(function (f) {
			return f.datatype === 'file';
		});
		if (!hasFileField) {
			return '';
		}
		var MAX = 10 * 1024 * 1024;
		var tooLargeMsg = JSON.stringify(T('form.fileUpload.tooLarge'));
		var failedMsg = JSON.stringify(T('form.fileUpload.failed'));
		var code = '<script>';
		code += '(function(){\n';
		code +=
			'function ready(fn){if(document.readyState!=="loading"){fn();}else{document.addEventListener("DOMContentLoaded",fn);}}\n';
		code += 'ready(function(){\n';
		code += 'var inputs=document.querySelectorAll("input[type=file][id^=up-file-input-]");\n';
		code += 'for(var k=0;k<inputs.length;k++){(function(input){\n';
		code += 'var token=document.getElementById(input.id.replace("up-file-input-","up-file-token-"));\n';
		code += 'input.addEventListener("change",function(){\n';
		code += 'token.value="";\n';
		code += 'var f=input.files&&input.files[0];if(!f){return;}\n';
		code +=
			'if(f.size>' +
			MAX +
			'){input.setCustomValidity(' +
			tooLargeMsg +
			');input.reportValidity();input.value="";return;}\n';
		code += 'input.setCustomValidity("");\n';
		code += 'var cid=document.getElementsByName("formCid")[0].value;\n';
		code += 'var fid=document.getElementsByName("formId")[0].value;\n';
		code += 'var fd=new FormData();fd.append("formCid",cid);fd.append("formId",fid);fd.append("file",f);\n';
		code += 'var btn=document.querySelector("#up-form button[type=submit]");if(btn){btn.disabled=true;}\n';
		code += 'fetch("' + '{{EXTERNAL_PATH}}' + 'forms/upload",{method:"POST",body:fd,credentials:"omit"})\n';
		code += '.then(function(r){return r.json().then(function(j){return {ok:r.ok,j:j};});})\n';
		code +=
			'.then(function(res){if(res.ok&&res.j&&res.j.data&&res.j.data.token){token.value=res.j.data.token;}else{input.setCustomValidity(' +
			failedMsg +
			');input.reportValidity();input.value="";}})\n';
		code +=
			'.catch(function(){input.setCustomValidity(' + failedMsg + ');input.reportValidity();input.value="";})\n';
		code += '.finally(function(){if(btn){btn.disabled=false;}});\n';
		code += '});\n';
		code += '})(inputs[k]);}\n';
		code += '});\n';
		code += '})();';
		code += '</script>';
		return code;
	}

	var getScripts = function (isDist, opts, form, hasPhoneFields) {
		var script = '<script>';
		script += 'var IS_SOCIAL_EVENT = ' + (opts.isSocialEvent ? 'true' : 'false') + ';\n';
		script += 'var eventIsFull = function(res) {\n';
		script += "if(IS_SOCIAL_EVENT && res && typeof res === 'object' && res.full) {\n";
		script += 'return true;\n';
		script += '}\n';
		script += 'return false;\n';
		script += '};\n';

		script += 'var eventIsCanceled = function(res) {\n';
		script += "if(IS_SOCIAL_EVENT && res && typeof res === 'object' && res.canceled) {\n";
		script += 'return true;\n';
		script += '}\n';
		script += 'return false;\n';
		script += '};\n';

		script += 'var eventIsPassed = function(res) {\n';
		script += "if(IS_SOCIAL_EVENT && res && typeof res === 'object' && res.passed && !res.isOnDemand) {\n";
		script += 'return true;\n';
		script += '}\n';
		script += 'return false;\n';
		script += '};\n';

		script += 'var showEventIsFull = function(res) {\n';
		script += 'if(res && res.waitingList) {\n';
		script += 'jQuery("#up-waiting-list-btn").show().click(function() {\n';
		script += 'jQuery("#up-form-event-full").css("display", "none");\n';
		script += 'jQuery("#up-form").css("display", "block");\n';
		script += '});\n';
		script += '}\n';
		script += 'jQuery("#up-form").css("display", "none");\n';
		script += 'jQuery("#up-form-event-full").css("display", "block");\n';
		script += 'jQuery("#up-event-full-text").text(res.msg||\'\');\n';
		script += 'jQuery("#up-form button[type=submit]").text(\'Sign up for the waiting list\');\n';
		script += '};\n';

		script += 'var showEventIsCanceled = function(res) {\n';
		script += 'if(res && res.canceled) {\n';
		script += 'jQuery("#up-event-canceled").css("display", "block");\n';
		script += '}\n';
		script += 'jQuery("#up-form").css("display", "none");\n';
		script += '};\n';

		script += 'var showEventIsPassed = function(res) {\n';
		script += 'if(res && res.passed) {\n';
		script += 'jQuery("#up-event-passed").css("display", "block");\n';
		script += '}\n';
		script += 'jQuery("#up-form").css("display", "none");\n';
		script += '};\n';

		script += 'var isOnDemand = false;\n';
		script +=
			'var isLandingPage = ' +
			(form && form.landingPageBody && form.landingPageBody.hasForm ? 'true' : 'false') +
			';\n';
		script += 'if(IS_SOCIAL_EVENT) {\n';
		script += '$.ajax({\n';
		script += 'type: "GET",\n';
		script += 'url: "{{EXTERNAL_PATH}}eventStatus/{{UUID}}",\n';
		script += 'success: function(res) {\n';
		script += 'isOnDemand = res.isOnDemand && res.passed;\n';
		script += 'if(eventIsPassed(res)) {\n';
		script += 'showEventIsPassed(res);\n';
		script += '} else if(eventIsFull(res)) {\n';
		script += 'showEventIsFull(res);\n';
		script += '} else if(eventIsCanceled(res)) {\n';
		script += 'showEventIsCanceled(res);\n';
		script += '} else {\n';
		script += 'jQuery("#up-form").css("display", "block");\n';
		script += '}\n';
		script += '},\n';
		script += 'error: function(res) {\n';
		script += 'if(eventIsFull(res)) {\n';
		script += 'showEventIsFull(res);\n';
		script += '} else if(eventIsCanceled(res)) {\n';
		script += 'showEventIsCanceled(res);\n';
		script += '} else {\n';
		script += 'jQuery("#up-form").css("display", "block");\n';
		script += '}\n';
		script += '}\n';
		script += '});\n';
		script += '}\n';
		script += 'jQuery(document).ready(function() {\n';
		script += 'if(!IS_SOCIAL_EVENT) {\n';
		script += 'jQuery("#up-form").css("display", "block");\n';
		script += '}\n';
		script += 'var calcDiff = function(elem) {\n';
		script += 'var momentDate = moment(elem.data("date"));\n';
		script += 'if(momentDate.isValid()) {\n';
		script += 'var ms = momentDate.utc().unix();\n';
		script += 'var curr = moment().utc().unix();\n';
		script += 'var diffTime = ms - curr;\n';
		script += 'var duration = moment.duration(diffTime*1000, "milliseconds");\n';
		script += "var days = momentDate.diff(moment(), 'days');\n";
		script += 'var hours = duration.hours();\n';
		script += 'var minutes = duration.minutes();\n';
		script += 'if(days < 0) {\n';
		script += 'days = 0;\n';
		script += '}\n';
		script += 'if(hours < 0) {\n';
		script += 'hours = 0;\n';
		script += '}\n';
		script += 'if(minutes < 0) {\n';
		script += 'minutes = 0;\n';
		script += '}\n';
		script += 'if(days.toString().length === 1) {\n';
		script += 'days = "0"+days;\n';
		script += '}\n';
		script += 'if(hours.toString().length === 1) {\n';
		script += 'hours = "0"+hours;\n';
		script += '}\n';
		script += 'if(minutes.toString().length === 1) {\n';
		script += 'minutes = "0"+minutes;\n';
		script += '}\n';
		script += 'elem.find(".cd-unit.days h2").text(days);\n';
		script += 'elem.find(".cd-unit.hours h2").text(hours);\n';
		script += 'elem.find(".cd-unit.minutes h2").text(minutes);\n';
		script += '}\n';
		script += '};\n';

		script += 'var initMap = function(elem) {\n';
		script += 'var data = elem.data();\n';
		script += 'var pos = {lat: data.lat, lng: data.lng};\n';
		script += 'var map = new google.maps.Map(elem.find(".map")[0], {\n';
		script += 'zoom: 16,\n';
		script += 'center: pos,\n';
		script += 'disableDefaultUI: true,\n';
		script += 'mapTypeControl: data.showtools,\n';
		script += 'zoomControl: data.showtools,\n';
		script += 'scrollwheel: data.allowscrollzoom,\n';
		script += '});\n';
		script += 'var marker = new google.maps.Marker({\n';
		script += 'position: pos,\n';
		script += 'map: map\n';
		script += '});\n';
		script += '};\n';

		script += 'var initVimeo = function(elem) {\n';
		script += 'var videoUrl = elem.data("src");\n';
		script += 'var endpoint = "http://www.vimeo.com/api/oembed.json";\n';
		script += 'var callback = "embedVimeo";\n';
		script += 'var playerId = elem.attr("id")+"_player";\n';
		script +=
			'var url = endpoint + "?url=" + encodeURIComponent(videoUrl) + "&callback=" + callback + "&player_id="+playerId+"&width=640";\n';
		script += 'var js = document.createElement("script");\n';
		script += 'js.setAttribute("type", "text/javascript");\n';
		script += 'js.setAttribute("src", url);\n';
		script += 'document.getElementsByTagName("head").item(0).appendChild(js);\n';
		script += '};\n';

		if (hasPhoneFields) {
			script += getTelInputInitScript();
		}

		// The saved html holds the values from when the page was published, so recalculate
		// on load and keep ticking — the smallest unit shown is minutes (WEB-8917).
		script += 'jQuery(".type-countdown").each(function() {\n';
		script += 'var countdownElem = jQuery(this);\n';
		script += 'calcDiff(countdownElem);\n';
		script += 'setInterval(function() {\n';
		script += 'calcDiff(countdownElem);\n';
		script += '}, 60000);\n';
		script += '});\n';

		script += 'jQuery(".type-map").each(function() {\n';
		script += 'initMap(jQuery(this));\n';
		script += '});\n';

		script += 'jQuery(".type-video").each(function() {\n';
		script += 'var elem = jQuery(this);\n';
		script += 'if(elem.data("type") === "vimeo") {\n';
		script += 'initVimeo(elem);\n';
		script += '}\n';
		script += '});\n';

		script += 'if(window.location.href.indexOf("upFormData") !== -1) {\n';
		script += fillFormFields.toString();
		script += '\nfillFormFields(window.location.href);\n';
		script += '}\n';

		script += 'if(window.location.href.indexOf("?valid=false") != -1) {\n';
		script +=
			'document.getElementById("failedId").innerHTML = "The form didnt validate, please check all fields."\n';
		script += '}\n';
		script += 'if(window.location.href.indexOf("?reCaptcha=false") != -1) {\n';
		script +=
			'document.getElementById("failedId").innerHTML = "The reCaptcha validation failed. Please try again."\n';
		script += '}\n';

		const validDomains = _.find(form.fields, { name: 'Prefs.verifyEmailDomain' });
		if (validDomains && isDist) {
			/// IF validation of email is activated
			script += 'var emailInput = jQuery("input[type=\'email\']");';
			script += "emailInput.on('input', function(event){";
			script += 'var email = event.target.value.toLowerCase();';
			script += 'var domain = email.split("@");';
			script += 'validEmail = true;';
			script += 'var validDomains = [';

			Parser.invalidDomains.forEach(function (domain, i) {
				var prefix = i ? ',' : '';
				var topDomain = '';
				if (domain.topLevel) {
					topDomain = domain.topLevel + '$';
				}
				script += prefix + '"@' + domain.name + '\\\\.' + topDomain + '"';
			});
			script += '];';
			script += 'if(domain.length > 1){';
			script += 'domain = domain[1];';
			script += 'for (var i = 0; i < validDomains.length; i++) {';
			script += 'var re = new RegExp(validDomains[i]);';
			script += 'if(re.test("@"+domain)){';
			script += 'validEmail = false;';
			script += 'break;';
			script += '}';
			script += '}';
			script += '}else {';
			script += 'domain = "";';
			script += 'email = "";';
			script += '}';
			script += 'if(validEmail || !email){';
			script += 'jQuery("#email-error-message").remove();';
			script += 'jQuery(".email").removeClass("has-error");';
			script += '}else {';
			script += "var errorMsg = jQuery('#email-error-message');";
			script += 'if(!errorMsg.length){';
			script += 'jQuery(".email").addClass("has-error");';
			script += `emailInput.after(\"<div id='email-error-message' class='error-message'><span class='top-carot'></span><p class='error-body'>${T(
				'form.editForm.businessEmail.warning.first'
			)} \"+domain+\" ${T(
				'form.editForm.businessEmail.warning.second'
			)}</p><b id='up-email-warning'>!</b></div>\");`;
			script += '}else {';
			script += `errorMsg.replaceWith(\"<div id='email-error-message' class='error-message'><span class='top-carot'></span><p class='error-body'>${T(
				'form.editForm.businessEmail.warning.first'
			)} \"+domain+\" ${T(
				'form.editForm.businessEmail.warning.second'
			)}</p><b id='up-email-warning'>!</b></div>\");`;
			script += '}';
			script += '}';
			script += '});';
		}

		if (!opts.noTags && !opts.noVisit) {
			script += '/*<VISIT_TRACKING_START>*/\n';
			script += 'var formIdElem = document.getElementsByName("formId");\n';
			script += 'var formCidElem = document.getElementsByName("formCid");\n';
			script += 'var getParsedCookie = function(id) {\n';
			script += 'return document.cookie.split(";").reduce(function(res, c) {\n';
			script += 'var parced = c.trim().split("=").map(decodeURIComponent);\n';
			script += 'var key = parced[0];\n';
			script += 'var value = parced[1];\n';
			script += 'try {\n';
			script += 'value = JSON.parse(value);\n';
			script += '} catch (e) {\n';
			script += '/* Not JSON */\n';
			script += '}\n';
			script += 'res[key] = value;\n';
			script += 'return res;\n';
			script += '}, {});\n';
			script += '}\n';
			script += 'var addVisitedId = function(id) {\n';
			script += 'var cookies = getParsedCookie()\n';
			script += 'var visitedIds = cookies.visitedIds || [];\n';
			script += 'visitedIds.push(id);\n';
			script += 'document.cookie = "visitedIds="+JSON.stringify(visitedIds);\n';
			script += '}\n';
			script += 'if(formIdElem && formIdElem.length && formCidElem && formCidElem.length) {\n';
			script += 'var formId = formIdElem[0].value;\n';
			script += 'var formCid = formCidElem[0].value;\n';
			script += 'var cookies = getParsedCookie();\n';
			script += 'if((cookies.visitedIds || []).indexOf(formId) === -1) {\n';
			script += 'var xmlhttp = new window.XMLHttpRequest();\n';
			script += 'xmlhttp.open("POST", "{{EXTERNAL_PATH}}formView/"+formId+"_"+formCid, true);\n';
			script += 'xmlhttp.send();\n';
			script += 'addVisitedId(formId);\n';
			script += '}\n';
			script += '}\n';
			script += '/*<VISIT_TRACKING_END>*/\n';
		}

		script += '});\n'; // end of document.ready

		script += 'function embedVimeo(video) {\n';
		script += 'var regex = /([0-9A-Z]+)_player/g;\n';
		script += 'var match = regex.exec(video.html);\n';
		script += 'if(match.length >= 2) {\n';
		script += 'jQuery("#"+match[1]).html(video.html);\n';
		script += '}\n';
		script += '}\n';
		script += 'var validEmail = true;';
		script += 'function validateForm(event, redirectURL) {\n';
		script += 'event.preventDefault();\n';
		script += "var formId = document.getElementsByName('formId')[0].value;\n";
		script += "var form = document.forms['form_'+formId];\n";
		script += "var inputs = form.querySelectorAll('input');\n";
		script += 'for(var i = 0; i < inputs.length; i++) {\n';
		script += "if(inputs[i].type === 'text') {\n";
		script += 'inputs[i].value = inputs[i].value.trim();\n';
		script += '}\n';
		script += 'if (inputs[i].iti) {' + 'inputs[i].value = inputs[i].iti.getNumber();' + '}';
		script += '}\n';

		script += 'if (!form.checkValidity() || !validEmail) {';
		script += 'return false;';
		script += '}';

		// reCAPTCHA v3: fetch token in the background before submitting
		script += 'var v3ModeEl = form.querySelector(\'input[name="Prefs.reCaptchaMode"]\');\n';
		script += 'var v3TokenEl = form.querySelector(\'input[name="Prefs.reCaptcha"]\');\n';
		script += 'var isV3 = v3ModeEl && v3ModeEl.value === "v3";\n';
		script += 'if (isV3 && v3TokenEl && !v3TokenEl.value) {\n';
		script += 'var v3FailEl = document.getElementById("failedId");\n';
		script += 'if (!window.grecaptcha || !grecaptcha.execute) {\n';
		script += 'if (v3FailEl) { v3FailEl.innerHTML = "The reCaptcha validation failed. Please try again."; }\n';
		script += 'return false;\n';
		script += '}\n';
		script += 'grecaptcha.ready(function() {\n';
		script += 'grecaptcha.execute("{{RECAPTCHA_V3_SITE}}", {action: "form_submit"}).then(function(token) {\n';
		script += 'v3TokenEl.value = token;\n';
		script += 'validateForm(event, redirectURL);\n';
		script += '}).catch(function() {\n';
		script += 'if (v3FailEl) { v3FailEl.innerHTML = "The reCaptcha validation failed. Please try again."; }\n';
		script += '});\n';
		script += '});\n';
		script += 'return false;\n';
		script += '}\n';
		script += 'var v3Ready = isV3 && v3TokenEl && v3TokenEl.value;\n';

		script += 'if(v3Ready || window.grecaptcha === undefined || grecaptcha.getResponse() !== "") {\n';
		script += 'jQuery("#up-form button[type=submit]").attr("disabled","disabled");\n';
		script += 'jQuery(".error-message").remove();\n';
		script += 'var data = $(form).serialize();\n';
		script += 'data += "&isAjax=true";\n';
		script += 'var _paq = window._paq || null;\n';
		script += '$.ajax({\n';
		script += 'type: "POST",\n';
		script += 'url: form.action,\n';
		script += 'data: data, \n';
		script += 'success: function(qs) {\n';
		script += 'if(!redirectURL) {\n';
		script += 'jQuery("#up-form button[type=submit]").removeAttr("disabled");\n';
		script += '}\n';
		script += 'if(eventIsFull(qs)) {\n';
		script += 'showEventIsFull(qs);\n';
		script += 'return;\n';
		script += '}\n';

		script += 'if(_paq && _paq.push) {\n';
		script += "_paq.push(['trackLink', \"https://post.upsales.com/\"+qs, 'link']);\n";
		script += '}\n';
		script += 'if(window._uaq) {\n';
		script += "window._uaq('form=' + qs);\n";
		script += '}\n';

		script += 'if(redirectURL) {\n';
		script += 'setTimeout(function() {';
		script += 'window.top.location.href = redirectURL;';
		script += '}, 333);';
		script += '} else {\n';
		script += 'jQuery("#up-form").css("display", "none");\n';
		script +=
			'jQuery((isOnDemand && !isLandingPage) ? "#up-form-thanks-on-demand" : "#up-form-thanks").css("display", "block");\n';
		script += '}\n';

		script += 'var upsalesEvent = {\n';
		script += "event: 'upsales.form_submitted',\n";
		script += 'formId: formId,\n';
		script += 'submitId: qs\n';
		script += '};\n';
		script += "window.parent.postMessage(upsalesEvent, '*');\n";
		script += 'if(window.top !== window.parent) {\n';
		script += "window.top.postMessage(upsalesEvent, '*');\n";
		script += '}\n';
		script += '},\n';
		script += 'error: function(res) {\n';
		script += "if(res && res.responseJSON && res.responseJSON.error === 'EVENT_FULL') {\n";
		script +=
			'jQuery("#up-form button[type=submit]").before(\'<div id="event-full-message" class="error-message"><p class="error-body">This event is full</p></div>\');';
		script += '}\n';
		script += "if(res && res.responseJSON && res.responseJSON.error === 'EVENT_CANCELED') {\n";
		script +=
			'jQuery("#up-form button[type=submit]").before(\'<div id="event-full-message" class="error-message"><p class="error-body">This event is canceled</p></div>\');';
		script += '}\n';
		script += "if(res && res.responseJSON && res.responseJSON.error === 'EVENT_CONTACT_DUPLICATE') {\n";
		script +=
			'jQuery("#up-form button[type=submit]").before(\'<div id="event-full-message" class="error-message"><p class="error-body">This email is already registered</p></div>\');';
		script += '}\n';
		// res.status === 0 means the XHR never reached the server — usually a
		// browser extension (AdBlock / EasyPrivacy / Brave shields) blocked it.
		// Without a message the click looks like a no-op to the prospect. See WEB-6682.
		script += 'if(res && res.status === 0) {\n';
		script += 'var __blockedLang = (navigator.language || navigator.userLanguage || "").toLowerCase();\n';
		script += 'var __blockedMsg = __blockedLang.indexOf("sv") === 0\n';
		script +=
			'  ? "Vi kunde inte skicka formuläret. Det här beror oftast på ett webbläsartillägg som AdBlock. Avaktivera tillägget och försök igen."\n';
		script +=
			'  : "We can\\\'t submit this form. This is usually caused by a third-party browser plugin like AdBlock. Please disable it and try again.";\n';
		script +=
			'jQuery("#up-form button[type=submit]").before(\'<div class="error-message"><p class="error-body">\' + __blockedMsg + \'</p></div>\');\n';
		script += '}\n';
		script += 'if(res && res.status === 400) {\n';
		script +=
			'jQuery("#up-form button[type=submit]").before(\'<div class="error-message"><p class="error-body">Something went wrong, please try again</p></div>\');';
		script += '}\n';
		script += 'jQuery("#up-form button[type=submit]").removeAttr("disabled");\n';
		script += '}\n';
		script += '});\n';
		script += '} else {\n';
		script += 'jQuery("#recaptcha-error").remove();';
		script += 'var recapElem = jQuery(".g-recaptcha");';
		script +=
			"recapElem.after(\"<div id='recaptcha-error' class='error-message'><span class='top-carot'></span><p class='error-body'>You need to enter this field</p></div>\");";
		script += 'return false;\n';
		script += '}\n';
		script += '}\n';

		script += getOpenTerms(opts);

		script += '</script>';
		return script;
	};

	function fillFormFields() {
		const url = new URL(window.location.href);
		const upFormData = url.searchParams.get('upFormData');
		const formData = JSON.parse(decodeURIComponent(upFormData));

		const firstNameField = window.document.getElementsByName('Contact.firstname')[0];
		if (formData.firstName && firstNameField) {
			firstNameField.value = formData.firstName;
		}
		const lastNameField = window.document.getElementsByName('Contact.lastname')[0];
		if (formData.lastName && lastNameField) {
			lastNameField.value = formData.lastName;
		}
		const emailField = window.document.getElementsByName('Contact.email')[0];
		if (formData.email && emailField) {
			emailField.value = formData.email;
		}
		const companyField = $('#up-client-name-input');
		if (formData.company && companyField) {
			companyField.val(formData.company);
			companyField.trigger('input');

			const dunsField = window.document.getElementsByName('Client.dunsNo')[0];
			if (formData.externalId && dunsField) {
				dunsField.value = formData.externalId;
			}
		}
	}

	// Shared builder for the company-name typeahead injected into published forms. Both the
	// bisnode/prospecting autocomplete and the company-from-category picker are the same widget
	// over #up-client-name-input; they differ only in the opts below. Keep them in sync here.
	var buildCompanyTypeaheadScript = function (opts) {
		// opts: { searchUrl, hiddenName, valueProp, initKey, enforceMinLen, browseOnFocus, requireSelection }
		var code = '';
		code += '<style>';
		code += '.up-company-search{position:relative;}';
		code +=
			'.up-company-search__menu{position:absolute;top:100%;left:0;right:0;margin:0;padding:0;background:#fff;max-height:300px;overflow:auto;border:1px solid #66afe9;border-top:none;border-radius:0 0 4px 4px;list-style:none;z-index:100;}';
		code += '.up-company-search__menu[hidden]{display:none;}';
		code += '.up-company-search__menu li{padding:5px 10px;color:#4B5562;cursor:pointer;}';
		code +=
			'.up-company-search__menu li.is-highlighted,.up-company-search__menu li:hover{background-color:#F6F9FB;color:#000;}';
		code += '.up-company-search__menu li span{display:block;color:#6B7C93;font-size:10px;}';
		code += '.up-company-search__spinner{position:absolute;right:8px;top:50%;transform:translateY(-50%);}';
		code += '</style>';
		code += '<script>';
		code += '(function(){\n';
		code += 'var SEARCH_URL=' + JSON.stringify(opts.searchUrl) + ';\n';
		code += 'var DEBOUNCE_MS=250;\n';
		code += 'var TIMEOUT_MS=5000;\n';
		if (opts.enforceMinLen) {
			code += 'var MIN_LEN=2;\n';
		}
		code += 'var LIMIT=25;\n';
		code +=
			'function ready(fn){if(document.readyState!=="loading")fn();else document.addEventListener("DOMContentLoaded",fn);}\n';
		code += 'ready(function(){\n';
		code += 'var input=document.getElementById("up-client-name-input");\n';
		code += 'if(!input||input.dataset.' + opts.initKey + '==="1")return;\n';
		code += 'input.dataset.' + opts.initKey + '="1";\n';
		code += 'input.setAttribute("autocomplete","off");\n';
		code += 'var wrapper=document.createElement("div");\n';
		code += 'wrapper.className="up-company-search";\n';
		code += 'input.parentNode.insertBefore(wrapper,input);\n';
		code += 'wrapper.appendChild(input);\n';
		code += 'var hidden=document.createElement("input");\n';
		code += 'hidden.type="hidden";\n';
		code += 'hidden.name=' + JSON.stringify(opts.hiddenName) + ';\n';
		code += 'wrapper.appendChild(hidden);\n';
		code += 'var spinner=document.createElement("b");\n';
		code += 'spinner.className="up-company-search__spinner";\n';
		code +=
			"spinner.innerHTML=\"<svg class='spinner' viewBox='0 0 50 50'><circle class='path' cx='25' cy='25' r='20' fill='none' stroke-width='5'></circle></svg>\";\n";
		code += 'spinner.style.display="none";\n';
		code += 'wrapper.appendChild(spinner);\n';
		code += 'var menu=document.createElement("ul");\n';
		code += 'menu.className="up-company-search__menu";\n';
		code += 'menu.setAttribute("role","listbox");\n';
		code += 'menu.hidden=true;\n';
		code += 'wrapper.appendChild(menu);\n';
		code += 'var selectedName="";\n';
		code += 'var items=[];\n';
		code += 'var highlighted=-1;\n';
		code += 'var debounceTimer=null;\n';
		code += 'var activeController=null;\n';
		code += 'var activeTimeout=null;\n';
		if (opts.requireSelection) {
			// The visible input is #up-client-name-input, i.e. the submitted Client.name. Typing a
			// company that isn't in the list must not submit, so mark the input invalid whenever it
			// holds text without a matching pick. Native constraint validation then blocks the
			// submit and shows the message, on both the standalone and the embedded form.
			code += 'var REQUIRE_MSG=' + JSON.stringify(T('form.companySelect.mustPickFromList')) + ';\n';
			code +=
				'function syncValidity(){input.setCustomValidity(!hidden.value&&input.value.trim()?REQUIRE_MSG:"");}\n';
		} else {
			code += 'function syncValidity(){}\n';
		}
		code += 'function hideMenu(){menu.hidden=true;highlighted=-1;}\n';
		code += 'function showMenu(){menu.hidden=items.length===0;}\n';
		code += 'function setSpinner(on){spinner.style.display=on?"inline":"none";}\n';
		code +=
			'function abortActive(){if(activeController){activeController.abort();activeController=null;}if(activeTimeout){clearTimeout(activeTimeout);activeTimeout=null;}}\n';
		code += 'function render(){\n';
		code += 'menu.innerHTML="";\n';
		code += 'items.forEach(function(item,idx){\n';
		code += 'var li=document.createElement("li");\n';
		code += 'li.setAttribute("role","option");\n';
		code += 'li.setAttribute("data-idx",String(idx));\n';
		code += 'var nameEl=document.createElement("div");\n';
		code += 'nameEl.textContent=item.name||"";\n';
		code += 'li.appendChild(nameEl);\n';
		code +=
			'if(item.city){var cityEl=document.createElement("span");cityEl.textContent=item.city;li.appendChild(cityEl);}\n';
		code += 'li.addEventListener("mousedown",function(ev){ev.preventDefault();select(item);});\n';
		code += 'menu.appendChild(li);\n';
		code += '});\n';
		code += 'showMenu();\n';
		code += '}\n';
		code += 'function select(item){\n';
		code += 'input.value=item.name||"";\n';
		code += 'hidden.value=item.' + opts.valueProp + '||"";\n';
		code += 'selectedName=item.name||"";\n';
		code += 'syncValidity();\n';
		code += 'hideMenu();\n';
		code += '}\n';
		code += 'function updateHighlight(){\n';
		code += 'var lis=menu.querySelectorAll("li");\n';
		code += 'for(var i=0;i<lis.length;i++){\n';
		code +=
			'if(i===highlighted){lis[i].classList.add("is-highlighted");lis[i].scrollIntoView({block:"nearest"});}\n';
		code += 'else lis[i].classList.remove("is-highlighted");\n';
		code += '}\n';
		code += '}\n';
		code += 'function search(q){\n';
		code += 'abortActive();\n';
		if (opts.enforceMinLen) {
			code += 'if(q.length<MIN_LEN){items=[];hideMenu();setSpinner(false);return;}\n';
		}
		code += 'setSpinner(true);\n';
		code += 'activeController=typeof AbortController!=="undefined"?new AbortController():null;\n';
		code += 'var signal=activeController?activeController.signal:undefined;\n';
		code += 'activeTimeout=setTimeout(function(){if(activeController)activeController.abort();},TIMEOUT_MS);\n';
		code += 'fetch(SEARCH_URL+encodeURIComponent(q),{signal:signal,credentials:"omit"})\n';
		code += '.then(function(res){return res.ok?res.json():Promise.reject(new Error("HTTP "+res.status));})\n';
		code += '.then(function(body){items=(body&&body.data?body.data:[]).slice(0,LIMIT);render();})\n';
		code += '.catch(function(err){if(err&&err.name==="AbortError")return;items=[];hideMenu();})\n';
		code +=
			'.then(function(){setSpinner(false);if(activeTimeout){clearTimeout(activeTimeout);activeTimeout=null;}});\n';
		code += '}\n';
		code += 'input.addEventListener("input",function(){\n';
		code += 'if(input.value!==selectedName)hidden.value="";\n';
		code += 'syncValidity();\n';
		code += 'if(debounceTimer)clearTimeout(debounceTimer);\n';
		code += 'var q=input.value;\n';
		code += 'debounceTimer=setTimeout(function(){search(q);},DEBOUNCE_MS);\n';
		code += '});\n';
		code += 'input.addEventListener("keydown",function(ev){\n';
		code += 'if(menu.hidden||items.length===0){if(ev.key==="Escape")hideMenu();return;}\n';
		code +=
			'if(ev.key==="ArrowDown"){ev.preventDefault();highlighted=Math.min(items.length-1,highlighted+1);updateHighlight();}\n';
		code +=
			'else if(ev.key==="ArrowUp"){ev.preventDefault();highlighted=Math.max(0,highlighted-1);updateHighlight();}\n';
		code += 'else if(ev.key==="Enter"&&highlighted>=0){ev.preventDefault();select(items[highlighted]);}\n';
		code += 'else if(ev.key==="Escape"){hideMenu();}\n';
		code += '});\n';
		code += 'input.addEventListener("blur",function(){setTimeout(hideMenu,150);});\n';
		if (opts.browseOnFocus) {
			code +=
				'input.addEventListener("focus",function(){if(items.length>0){showMenu();}else if(!input.value){search("");}});\n';
		} else {
			code += 'input.addEventListener("focus",function(){if(items.length>0)showMenu();});\n';
		}
		if (opts.requireSelection) {
			// The browser autofills autocomplete="organization" before this script runs, and can set
			// the value without an input event, so seed the state here too rather than only on typing.
			code += 'input.addEventListener("change",syncValidity);\n';
			code += 'syncValidity();\n';
		}
		code += '});\n';
		code += '})();\n';
		code += '</script>';
		return code;
	};

	var getCompanySearchScripts = function () {
		const hasProspecting = isAvailable(Feature.PROSPECTING_BASIC);
		let searchUrl;
		if (hasProspecting) {
			const availableCountries = getAvailableCountries();
			const countryParams = availableCountries.map(country => `country=${country}`).join('&');
			searchUrl = `{{EXTERNAL_PATH}}forms/companyLookup?${countryParams}&name=`;
		} else {
			searchUrl = '{{EXTERNAL_PATH}}forms/basicCompanyLookup?name=';
		}

		return buildCompanyTypeaheadScript({
			searchUrl: searchUrl,
			hiddenName: 'Client.dunsNo',
			valueProp: 'dunsNo',
			initKey: 'upCompanySearchInit',
			enforceMinLen: true,
			browseOnFocus: false
		});
	};

	var getCompanySelectScripts = function (form) {
		var companyField = _.find(form.fields, { name: 'Client.name' });
		if (!companyField) {
			return '';
		}
		// Only a guard that a category is configured at all — the ids themselves are not sent.
		// The endpoint reads them off the stored form, so the published page cannot influence
		// which categories are searched, and a form keeps working after its categories change
		// without being republished.
		if (!String(companyField.options || '').trim()) {
			return '';
		}
		var searchUrl = '{{EXTERNAL_PATH}}forms/companySelect?formCid={{CUSTOMER_ID}}&formId={{UUID}}&name=';

		// When the pick drives account matching, a typed company that isn't in the category would
		// either create a junk account or fuzzy-match onto the wrong one, so a pick is mandatory.
		// With matching off the field is only a convenience typeahead over the plain company name
		// (WEB-7384), and free text stays allowed.
		var requireSelection = !!_.find(form.fields, { name: 'Prefs.matchOnCompanySelect' });

		return buildCompanyTypeaheadScript({
			searchUrl: searchUrl,
			hiddenName: 'Client.id',
			valueProp: 'id',
			initKey: 'upCompanyCatInit',
			enforceMinLen: false,
			browseOnFocus: true,
			requireSelection: requireSelection
		});
	};

	var wrap = function (convertedDom, accountProfile, isDist, form, opts, cb) {
		var head = '';
		var body = '';
		var html;

		// Guard: customers without an AccountProfile pass null here. Treat as empty so
		// downstream `.fonts` / `.something` accesses don't blow up.
		accountProfile = accountProfile || {};

		var headElem = convertedDom.find(selectors.HEAD);
		if (headElem.length) {
			head = headElem.html();
		} else {
			return cb('Missing ' + selectors.HEAD);
		}

		var bodyElem = convertedDom.find(selectors.BODY);
		var bisnodeAutocomplete = _.find(form.fields, { name: 'Prefs.bisnodeAutocomplete' });
		var hasCompanySelect = _.find(form.fields, { name: 'Prefs.companyFromCategory' });

		if (!opts.embedded) {
			head += getDefaultStyle(form);
			head += getCustomStyle(form);
		}

		if (bodyElem.length) {
			if (opts.embedded) {
				if (hasCompanySelect) {
					body += getCompanySelectScripts(form) + '\n';
				} else if (bisnodeAutocomplete) {
					body += getCompanySearchScripts() + '\n';
				}
				body += '<script type="text/javascript">';
				body += getOpenTerms(opts);
				body += '</script>';

				body += bodyElem.find('.up-form-wrapper').html();
				return cb(null, body);
			}
			body = bodyElem.html();
		} else {
			return cb('Missing ' + selectors.BODY);
		}

		if (!isDist) {
			var scopedStyle = '<style id="template-parser-scoped-style">';

			var headStyles = headElem.find('style');
			var fonts = accountProfile.fonts;
			if (headStyles.length) {
				scopedStyle += headStyles.html();
			}

			scopedStyle += getStyle(accountProfile, true, isDist, opts.isPreview || !isDist);
			scopedStyle += '</style>';

			if (!opts.embedded) {
				scopedStyle += getDefaultStyle(form);
				scopedStyle += getCustomStyle(form);
			}

			if (fonts && fonts.length) {
				root.WebFont.load({
					google: {
						families: accountProfile.fonts.map(function (font) {
							return font.family;
						})
					}
				});
			}

			html = '<div class="template-scoped-style">';
			html += scopedStyle;
			html += body;
			html += '</div>';
			return cb(null, html);
		}

		head += getBootstrap();
		head += getCustomFonts(accountProfile.fonts);

		head += '<title>' + escapeHtml(form.name) + '</title>';

		if (convertedDom.find('input[name="Prefs.reCaptchaMode"][value="v3"]').length) {
			head += '<script src="https://www.google.com/recaptcha/api.js?render={{RECAPTCHA_V3_SITE}}"></script>';
			head += '<style>.grecaptcha-badge{visibility:hidden;}</style>';
		} else if (convertedDom.find('.g-recaptcha').length) {
			head += '<script src="https://www.google.com/recaptcha/api.js"></script>';
			head +=
				'<style>.g-recaptcha-bubble-arrow + div:not([class]) {transform:translate3d(0,0,0) !important;}</style>';
		}

		html = '';
		html += '<!doctype html><html prefix="og: http://ogp.me/ns#"><head>';
		html += '<meta charset="utf-8"/>';
		html += '<meta name="robots" content="noindex">';

		// need to map the current dom to form to get correct social media tags and stuff
		form.html = convertedDom.html();
		html += buildSocialMediaLinks(isDist, opts, form);
		html += head;
		html += getStyle(accountProfile, false, isDist, opts.isPreview || !isDist);

		var customScripts = '';
		var scriptField = _.find(form.fields, { name: 'Prefs.js' });

		if (scriptField && !opts.noScript) {
			customScripts = scriptField.options;
		}

		html += '<script src="https://code.jquery.com/jquery-3.7.1.min.js"></script>';

		if (isDist && hasCompanySelect) {
			html += getCompanySelectScripts(form);
		} else if (isDist && bisnodeAutocomplete) {
			html += getCompanySearchScripts(form);
		}

		if (form.landingPageBody) {
			html +=
				'<link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/font-awesome/latest/css/font-awesome.min.css"/>';
			if (bodyElem.find('.type-map').length) {
				// Find the used domain
				const domain = opts.domains && form.domain ? _.find(opts.domains, { name: form.domain }) : null;
				const gmapKey = getGmapKey(domain);

				html += `<script src="https://maps.googleapis.com/maps/api/js?key=${gmapKey}&libraries=places&v=3"></script>`;
			}
			if (bodyElem.find('.type-countdown').length) {
				html += '<script src="/static/moment.min.js"></script>';
			}
		}

		const hasPhoneFields = form.fields.find(field => (field.name || '').toLowerCase().includes('phone'));
		if (hasPhoneFields) {
			html += getIntlTelLibrary();
		}

		html += getScripts(isDist, opts, form, hasPhoneFields);
		html += getFileUploadScript(form);
		html += customScripts;

		html += '<meta name="viewport" content="width=device-width, initial-scale=1">';
		html += '</head>';
		html += '<body class="template-scoped-style" style="background-color: {{BODY_BG}};">';

		html += '<!--TEMPLATE_START-->';
		html += body;
		html += '<!--TEMPLATE_END-->';
		if (!opts.noTags && !opts.noVisit) {
			html += '{{VISITOR_SCRIPT}}';
		}

		html += '</body>';
		html += '</html>';
		cb(null, html);
	};

	var getRows = function (dom) {
		var rows = dom.find(selectors.ROW);
		return rows;
	};

	function getBlankMarkdown() {
		return '<div data-up-html><div data-up-head></div><div data-up-body></div></div>';
	}

	async function parseFunction(form, accountProfile, isDist, options, callback) {
		loadBusinessEmail();
		if (callback === undefined) {
			callback = accountProfile;
			accountProfile = undefined;
		}

		var config = form.landingPageBody;
		var dom = jQuery('<div id="wrapper"></div>');
		var markdown;

		if (config) {
			markdown = config.markdown;
		} else {
			markdown = getBlankMarkdown();
		}

		dom.append(markdown);

		// If form
		if (!config) {
			var formWrap = jQuery('<div />');
			formWrap.css({
				width: '400px',
				margin: '30px auto',
				'max-width': '100%'
			});

			parseElement(formWrap, 'up-form-element-wrapper', { type: 'form' }, false, isDist, form, options);
			dom.find(selectors.BODY).append(formWrap);

			wrap(dom, accountProfile, isDist, form, options, function (err, html) {
				callback(err, html);
			});

			return;
		}

		// If event location. Get location data
		getLocaionData(config.rows, options, function () {
			var rows = getRows(dom);
			rows.each(function (i, row) {
				row = jQuery(row);
				var rowId = row.attr('data-row-id');
				var rowConfig = _.find(config.rows, function (r) {
					// eslint-disable-next-line
					return rowId == r.id;
				});
				var rowHtml = row.html();
				var wrappedRow = jQuery('<div/>')
					.addClass('row up-row')
					.attr({ 'data-row-id': rowConfig.id, 'data-up-row': '' });
				var container = jQuery('<div/>').addClass('container up-container').html(rowHtml);
				wrappedRow.attr('id', row.attr('id'));
				wrappedRow.append(container);

				if (row.attr('data-up-special') !== undefined) {
					wrappedRow.attr({
						id: row.attr('id'),
						'data-up-special': ''
					});

					if (rowConfig.transparentHeader || !rowConfig.visible) {
						wrappedRow.addClass('up-absolute-row');
					}

					var rowElements = wrappedRow.find(selectors.ELEMENT);
					// Parse row child elements
					rowElements.each(function (i, elem) {
						elem = jQuery(elem);
						var id = elem.attr('data-up-id');
						var elementConf = _.find(rowConfig.elements, function (e) {
							return id.toString() === e.id.toString();
						});
						var hasParent = elem.parent().attr('id') === id + '-wrapper';
						if (elementConf && (elementConf.required || elementConf.enabled)) {
							wrappedRow.addClass('has-' + id);
							parseElement(elem, id, elementConf, hasParent, isDist, form, options);
						} else {
							if (hasParent) {
								elem.parent().remove();
							} else {
								elem.remove();
							}
						}
					});

					if (rowConfig.style) {
						container.css(rowConfig.style);
					}

					if (rowConfig.padding) {
						wrappedRow.addClass('vpadding-' + rowConfig.padding.top);
						wrappedRow.addClass('hpadding-' + rowConfig.padding.left);
					}
				}

				row.replaceWith(wrappedRow);
				row = wrappedRow;

				// Parse row element
				parseRow(row, rowConfig);

				var columns = row.find(selectors.COLUMN);
				var colTot = 0;
				_.each(columns, function (col) {
					var colspan = parseInt(jQuery(col).attr('data-up-col'));
					if (isNaN(colspan) || !colspan) {
						colTot += 1;
					} else {
						colTot += colspan;
					}
				});

				_.each(columns, function (col, i) {
					var column = jQuery(col);
					var colConfig = rowConfig.columns[i];
					column.attr('data-col-id', colConfig.id);
					var colspan = parseInt(jQuery(col).attr('data-up-col'));
					if (isNaN(colspan) || !colspan) {
						colspan = 1;
					}
					var colClass = 'col-md-' + (12 / colTot) * colspan;
					column.addClass(colClass);
					column.addClass('up-column');
					column.css(colConfig.style);

					if (colConfig.padding) {
						column.addClass('vpadding-' + colConfig.padding.top);
						column.addClass('hpadding-' + colConfig.padding.left);
					}

					var upElements = column.find(selectors.ELEMENT);
					if (!upElements.length && !isDist) {
						var noContent = jQuery('<div/>');
						noContent.addClass('no-content-element');
						noContent.html('<b class="fa fa-plus"></b>');
						column.append(noContent);
					} else {
						// Parse row child elements
						upElements.each(function (i, elem) {
							elem = jQuery(elem);
							var id = elem.attr('data-up-id');
							var elementConf = _.find(colConfig.elements, function (e) {
								return id.toString() === e.id.toString();
							});
							var hasParent = elem.parent().attr('id') === id + '-wrapper';
							if (elementConf && (elementConf.required || elementConf.enabled)) {
								row.addClass('has-' + id);
								var placeholder = parseElement(elem, id, elementConf, hasParent, isDist, form, options);
								if (!isDist && placeholder) {
									elem.append(placeholder);

									elem.css('display', 'none');
									placeholder.insertAfter(elem);
								}
							} else {
								if (hasParent) {
									elem.parent().remove();
								} else {
									elem.remove();
								}
							}
						});
					}
				});
			});

			wrap(dom, accountProfile, isDist, form, options, function (err, html) {
				callback(err, html);
			});
		});
	}

	var getElementValue = function (type, elem) {
		var val = {};
		switch (type) {
			case 'text':
			case 'button':
				var contentElem = elem.find(selectors.CONTENT);
				if (contentElem.length) {
					val.text = contentElem.html();
				}
				val.text = elem.html();
				break;

			case 'image':
				val.src = elem.attr('data-src') || '';
				val.opacity = 100;
				val.align = elem.attr('data-align') || 'left';
				break;

			case 'video':
				val.src = elem.attr('data-src') || '';
				val.allowFullScreen = true;
				val.shadow = true;
				break;

			case 'countdown':
				var date = elem.attr('data-date');
				var dateTag = elem.attr('data-date-tag');
				if (moment(date).isValid()) {
					val.date = moment(date).toDate();
				}
				if (dateTag && dateTag === '{{Event.DateIso}}') {
					val.dateTag = '{{Event.DateIso}}';
				}
				val.textColor = elem.attr('data-color') || '#6B7C93';
				val.numberColor = elem.attr('data-num-color') || '#000000';
				val.backgroundColor = elem.attr('data-background') || '#ffffff';
				break;

			case 'file':
				val.src = elem.attr('data-src') || '';
				val.fileName = elem.attr('data-fileName') || '';
				val.title = elem.attr('data-title') || '';
				val.textColor = elem.attr('data-color') || '#000000';
				val.buttonText = elem.attr('data-buttonText') || '';
				val.buttonTextColor = elem.attr('data-btn-color') || '#ffffff';
				val.buttonBackgroundColor = elem.attr('data-btn-background') || '#002f65';
				val.asImage = false;
				val.imgSrc = elem.attr('data-imgSrc') || '';
				break;
			case 'map':
				val.address = elem.attr('data-address') || '';
				val.lat = elem.attr('data-lat') || 0;
				val.lng = elem.attr('data-lng') || 0;
				val.showAddress = elem.attr('data-showaddress') || false;
				val.showTools = elem.attr('data-showtools') || true;
				val.allowScrollZoom = elem.attr('data-allowscrollzoom') || false;
				val.openMapsOnClick = false;
				break;
			case 'landingPage':
				val.href = '';
				break;
			case 'card':
				val.imgSrc = elem.attr('data-src') || '';
				val.imgOpacity = 100;
				val.title = elem.attr('data-title') || '';
				val.subtitle = elem.attr('data-subtitle') || '';
				val.text = elem.attr('data-text') || '';
				val.backgroundColor = elem.attr('data-background') || '#ffffff';
				val.textColor = elem.attr('data-color') || '#6B7C93';
				val.titleColor = elem.attr('data-title-color') || '#000000';
				break;
			case 'links':
				val.options = [];
				elem.find('li').each(function () {
					var a = jQuery(this).find('a');
					val.options.push({
						title: a.text(),
						href: a.attr('href') || ''
					});
				});
				break;
		}
		return val;
	};

	var translateProfileTags = function (markdown, accountProfile) {
		var tags = markdown.match(/{{(.*?)}}/g);

		if (!tags || !tags.length) {
			return markdown;
		}

		tags.forEach(function (tag) {
			var lookupTag = tag.replace('{{', '').replace('}}', '');
			if (tagsMap[lookupTag]) {
				var value = tagsMap[lookupTag](accountProfile);
				markdown = markdown.replace(tag, value);
			}
		});

		return markdown;
	};

	function translateLiveTags(markdown, liveTags) {
		liveTags = liveTags || {};
		var tags = markdown.match(/{{(.*?)}}/g);

		if (!tags || !tags.length) {
			return markdown;
		}

		tags.forEach(function (tag) {
			var lookupTag = tag.replace('{{', '').replace('}}', '');
			if (liveTags[lookupTag] && liveTags[lookupTag].value !== undefined) {
				markdown = markdown.replace(tag, liveTags[lookupTag].value || '');
				// It is not valid HTML to have a div inside a p tag as it is not "phratsing content" https://html.spec.whatwg.org/multipage/dom.html#phrasing-content-2
				// Thats why I replace the div with a span here
				if (
					lookupTag === 'Event.Description' &&
					/<p(.*?)<div class="social-event-description">(.*?)<\/div>(.*?)<\/p>/s.test(markdown)
				) {
					markdown = markdown.replace(
						/<div class="social-event-description">(.*?)<\/div>/gs,
						'<span class="social-event-description">$1</span>'
					);
				}
			}
		});

		return markdown;
	}

	function inheritStylesFromChildren(markdown) {
		const elementsThatWillInherit = ['li', 'ul'];
		if (markdown.length > 2000) {
			// Early return due to potential performance issues (regex), and i hope noone tries to have a bulletlist with 2000 characters...
			// If changing this code to include other things than bulletlists, make sure to check that this restriction is still valid
			return markdown;
		}

		/*
			Markdown will be something similar to:

			<ul>
				<li><span style="color:#f5a623;">g</span></li>
				<li><span style="color:#f5a623;">j</span></li>
			</ul>

			We want to find all the elements in "elementsThatWillInherit" - in this case we want to find the <li> elements - and then change the inline style of those elements to the style
			of the immediate child - in this case the span elements inside the <li> elements have a color, the <li> elements do not have a color, so we want to add the color to the <li> elements
			If the li elements dont have any inline styles, we want to add the inline styles of the child to the parent
			If the li elements already have some inline styles, we want to ignore it and move on to the next one
			We also want to find the <ul> elements and add the styles of the children to the parent
			so in this case we want to add the color of the now <li> elements to the <ul> element
		*/

		// Since markdown is a string its passed by value, so we dont need to worry about changing the original markdown
		let newMarkdown = markdown;

		for (const element of elementsThatWillInherit) {
			if (!markdown.includes(`<${element}`)) {
				continue;
			}
			const elementRegex = new RegExp(`<${element}[^>]*>.*?<\/${element}>`, 'gms');
			const parentRegex = new RegExp(`<${element}[^>]*>`, 'gms');
			const elementMatches = newMarkdown.match(elementRegex);
			if (elementMatches) {
				elementMatches.forEach(match => {
					// Find all style attributes in children
					const allChildStyles = [...match.matchAll(/style="([^"]*)"/g)];
					if (allChildStyles.length) {
						// Combine all unique styles
						const combinedStyles = new Set();
						allChildStyles.forEach(style => {
							if (!style?.[1]) {
								return;
							}
							style[1]
								.split(';')
								.filter(s => s.trim())
								.forEach(s => combinedStyles.add(s.trim()));
						});

						const parentElement = match.match(parentRegex);
						if (!parentElement || !parentElement[0]) {
							return;
						}
						const parentStyle = parentElement[0].match(/style="([^"]*)"/);
						if (!parentStyle) {
							const newParent = parentElement[0].replace(
								`<${element}`,
								`<${element} style="${Array.from(combinedStyles).join(';')}"`
							);
							newMarkdown = newMarkdown.replace(parentElement[0], newParent);
						}
					}
				});
			}
		}

		return newMarkdown;
	}

	var getRowConfig = function () {
		return {
			index: 0,
			id: 0,
			columns: [],
			elements: [],
			animation: null,
			special: false,
			visible: true,
			padding: {
				left: 'sm',
				right: 'sm',
				top: 'sm',
				bottom: 'sm'
			},
			background: {
				color: '#fff',
				img: null,
				imgOpacity: 100,
				imgBlur: 0,
				fullWidth: false,
				imgSize: 'cover',
				imgRepeat: 'no-repeat'
			}
		};
	};

	var getColConfig = function () {
		return {
			id: 0,
			index: 0,
			elements: [],
			padding: {
				left: 'sm',
				right: 'sm',
				top: 'sm',
				bottom: 'sm'
			},
			style: {}
		};
	};

	var getPadding = function (padding) {
		if (!padding || padding === '') {
			return 'sm';
		}

		return paddings[padding.toUpperCase()] || 'sm';
	};

	function generateConfig(markdown, accountProfile) {
		if (accountProfile) {
			markdown = translateProfileTags(markdown, accountProfile);
		}

		var formConfig = {
			buttonBgColor: getProfileProp(accountProfile, 'buttonBg', '#080808'),
			buttonTextColor: getProfileProp(accountProfile, 'buttonText', '#ffffff'),
			backgroundColor: getProfileProp(accountProfile, 'contentBg', '#ffffff'),
			textColor: getProfileProp(accountProfile, 'contentText', '#000000')
		};

		var config = {
			title: '',
			landingPage: '',
			backgroundColor: '#ffffff',
			rows: [],
			availableFormats: [],
			hasForm: false
		};

		var dom = jQuery('<div id="wrapper" class="wrapper"></div>');
		dom.append(markdown);

		var rows = getRows(dom);

		var bodyBg = dom.find(selectors.BODY).css('background-color');
		if (bodyBg) {
			config.backgroundColor = bodyBg;
		}
		var indexKey = 0;
		rows.each(function (i, row) {
			var rowConfig = getRowConfig();
			rowConfig.id = i;
			rowConfig.index = i;

			row = jQuery(row);

			row.attr('data-row-id', rowConfig.id);

			if (row.attr('data-up-special') !== undefined) {
				rowConfig.special = true;
				var paddingV = 'sm';
				var paddingH = 'sm';
				if (row.attr('data-up-padding') !== undefined) {
					var splt = _.trim(row.attr('data-up-padding')).split(' ');
					if (splt.length === 1) {
						paddingV = paddingH = getPadding(splt[0]);
					} else {
						paddingV = getPadding(splt[0]);
						paddingH = getPadding(splt[1]);
					}
				}
				rowConfig.padding = {
					left: paddingH,
					right: paddingH,
					top: paddingV,
					bottom: paddingV
				};
				if (row.attr('data-up-lang') !== undefined) {
					rowConfig.lang = row.attr('data-up-lang');
				} else {
					rowConfig.lang = '';
				}
				if (row.attr('data-up-transparent-header') !== undefined) {
					rowConfig.transparentHeader = true;
					rowConfig.transparentHeaderOption = true;
				} else {
					rowConfig.transparentHeader = false;
					rowConfig.transparentHeaderOption = false;
				}
				var rowElements = row.find(selectors.ELEMENT);
				// Parse row child elements
				rowElements.each(function (i, elem) {
					elem = jQuery(elem);
					var type = elem.attr('data-up-element');
					var id = elem.attr('data-up-id') || Date.now() + i;
					var elemConfig = {
						id: id,
						type: type,
						value: getElementValue(type, elem),
						style: {},
						enabled: true,
						animation: null,
						required: false,
						lang: elem.attr('data-up-lang') || 'bannerEditor.type.' + type
					};

					elem.attr('data-up-id', id);

					var color = elem[0].style.color;
					if (color && color.length) {
						elemConfig.style.color = color;
					}

					var backgroundColor = elem[0].style.backgroundColor;
					if (backgroundColor && backgroundColor.length) {
						elemConfig.style.backgroundColor = backgroundColor;
					}

					var required = elem.attr('data-up-required');
					if (required !== undefined) {
						elemConfig.required = true;
					}

					rowConfig.elements.push(elemConfig);
				});
			}

			var fullWidth = row.attr('data-fullw');
			if (fullWidth !== undefined) {
				rowConfig.background.fullWidth = true;
			}
			var color = row.attr('data-color');
			if (color) {
				rowConfig.background.color = color;
			}
			var img = row.attr('data-img');
			if (img) {
				rowConfig.background.img = img;
			}
			var parsed;
			var opacity = row.attr('data-opacity');
			if (opacity !== undefined) {
				parsed = parseInt(opacity);
				if (!isNaN(parsed)) {
					rowConfig.background.imgOpacity = parsed;
				}
			}
			var blur = row.attr('data-blur');
			if (blur !== undefined) {
				parsed = parseInt(blur);
				if (!isNaN(parsed)) {
					rowConfig.background.imgBlur = blur;
				}
			}

			var columns = row.find(selectors.COLUMN);
			columns.each(function (index, col) {
				var colConfig = getColConfig();
				colConfig.id = index;
				colConfig.index = index;
				rowConfig.columns.push(colConfig);

				var column = jQuery(col);
				var upElements = column.find(selectors.ELEMENT);
				column.attr('data-col-id', colConfig.id);

				var paddingV = 'sm';
				var paddingH = 'sm';
				if (column.attr('data-up-padding') !== undefined) {
					var splt = _.trim(column.attr('data-up-padding')).split(' ');
					if (splt.length === 1) {
						paddingV = paddingH = getPadding(splt[0]);
					} else {
						paddingV = getPadding(splt[0]);
						paddingH = getPadding(splt[1]);
					}

					colConfig.padding = {
						left: paddingH,
						right: paddingH,
						top: paddingV,
						bottom: paddingV
					};
				}

				// Parse row child elements
				upElements.each(function (i, elem) {
					indexKey++;
					elem = jQuery(elem);
					var type = elem.attr('data-up-element');
					var id = elem.attr('data-up-id') || Date.now() + indexKey;
					var elemConfig = {
						id: id,
						type: type,
						value: getElementValue(type, elem),
						style: {},
						enabled: true,
						animation: null,
						required: false,
						lang: elem.attr('data-up-lang') || 'bannerEditor.type.' + type
					};

					elem.attr('data-up-id', id);

					if (type === 'form') {
						config.hasForm = true;

						if (elem.attr('data-background')) {
							formConfig.backgroundColor = elem.attr('data-background');
						}

						if (elem.attr('data-color')) {
							formConfig.textColor = elem.attr('data-color');
						}

						if (elem.attr('data-button-bg')) {
							formConfig.buttonBgColor = elem.attr('data-button-bg');
						}

						if (elem.attr('data-button-color')) {
							formConfig.buttonTextColor = elem.attr('data-button-color');
						}

						if (elem.attr('data-title')) {
							formConfig.title = elem.attr('data-title');
						}

						if (elem.attr('data-description')) {
							formConfig.description = elem.attr('data-description');
						}
					}

					var color = elem[0].style.color;
					if (color && color.length) {
						elemConfig.style.color = color;
					}

					var backgroundColor = elem[0].style.backgroundColor;
					if (backgroundColor && backgroundColor.length) {
						elemConfig.style.backgroundColor = backgroundColor;
					}

					var required = elem.attr('data-up-required');
					if (required !== undefined) {
						elemConfig.required = true;
					}

					colConfig.elements.push(elemConfig);
				});
			});

			config.rows.push(rowConfig);
		});

		config.markdown = dom.html();

		formConfig.landingPageBody = config;

		// should generate a configJson from a markdown
		return formConfig;
	}

	function clearColumn(rowId, columnId, config, callback) {
		var rowConfig = _.find(config.rows, { id: parseInt(rowId) });
		var colConfig = _.find(rowConfig.columns, { id: parseInt(columnId) });
		var hadForm = _.find(colConfig.elements, { type: 'form' });
		colConfig.elements = [];
		var dom = jQuery('<div></div>');
		dom.append(config.markdown);

		var row = dom.find('[data-row-id=' + rowId + ']');
		row.find('[data-col-id=' + columnId + ']').html('');

		config.markdown = dom.html();

		if (hadForm) {
			config.hasForm = false;
		}

		callback(null, config);
	}

	function deleteRow(rowId, config, callback) {
		var index = _.findIndex(config.rows, { id: parseInt(rowId) });
		var rowConfig = config.rows[index];
		var dom = jQuery('<div></div>');
		dom.append(config.markdown);
		dom.find('[data-row-id=' + rowConfig.id + ']').remove();

		_.each(rowConfig.columns, function (colConfig) {
			var hadForm = _.find(colConfig.elements, { type: 'form' });
			if (hadForm) {
				config.hasForm = false;
			}
		});

		config.rows.splice(index, 1);

		config.markdown = dom.html();

		callback(null, config);
	}

	var setIndexOrder = function (rows) {
		_.each(rows, function (r, i) {
			r.index = i;
		});
	};

	function addRow(afterRowId, layout, config, callback) {
		var rowConfig = getRowConfig();
		rowConfig.id = Date.now();
		rowConfig.background.fullWidth = true;

		var row = jQuery('<div data-up-row></div>');
		row.attr('data-row-id', rowConfig.id);

		_.each(layout, function (colspan, i) {
			var colConfig = getColConfig();
			colConfig.id = i;
			colConfig.index = i;
			rowConfig.columns.push(colConfig);

			var column = jQuery('<div></div>');
			column.attr('data-up-col', colspan || '');
			column.attr('data-col-id', colConfig.id);

			row.append(column);
		}).join('');

		var dom = jQuery('<div></div>');
		dom.append(config.markdown);
		if (afterRowId === null) {
			// prepend row
			rowConfig.index = 0;
			config.rows.unshift(rowConfig);
			var rows = dom.find('[data-up-row]');
			if (rows.length) {
				rows.eq(0).before(row);
			} else {
				dom.find('[data-up-body]').append(row);
			}
		} else {
			dom.find('[data-row-id=' + afterRowId + ']').after(row);
			var rowIndex = _.findIndex(config.rows, { id: parseInt(afterRowId) });
			config.rows.splice(rowIndex + 1, 0, rowConfig);
		}

		config.markdown = dom.html();

		setIndexOrder(config.rows);

		callback(null, config);
	}

	function getElementConfig(type) {
		var elemConf = {
			id: Date.now(),
			type: type,
			value: null,
			style: {},
			enabled: true,
			animation: null,
			required: false
		};

		switch (type) {
			case 'video':
				elemConf.value = {
					src: '',
					allowFullScreen: true,
					shadow: true
				};
				break;
			case 'image':
				elemConf.value = {
					src: '',
					opacity: 100,
					shadow: true,
					align: 'center'
				};
				break;
			case 'text':
				elemConf.value = {
					text: '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi vestibulum sapien ac accumsan hendrerit. Praesent euismod, ex sed vehicula mattis, nibh enim sagittis nulla, at imperdiet odio eros sed eros. Proin accumsan dui id velit egestas scelerisque. In quis eleifend urna, vel ornare massa. Curabitur et orci dolor. Nam lorem est, sollicitudin non mattis et, faucibus at velit. In rutrum augue libero, ac venenatis libero condimentum nec. Sed sodales lacinia sodales. Curabitur ac rhoncus enim.</p>'
				};
				break;
			case 'button':
				elemConf.value = {
					title: '',
					titleColor: '#000000',
					text: Parser.translate('mail.text'),
					link: '',
					buttonTextColor: '#ffffff',
					buttonBackgroundColor: '#002f65',
					buttonBorderColor: '#002f65',
					srcTarget: '_blank',
					align: 'center',
					paddingH: '12',
					paddingV: '6',
					fontSize: '14',
					fullWidth: false
				};
				break;
			case 'countdown':
				elemConf.value = {
					date: new Date(),
					dateTag: null,
					numberColor: '#000000',
					textColor: '#6B7C93',
					backgroundColor: '#ffffff'
				};
				break;
			case 'file':
				elemConf.value = {
					src: '',
					fileName: '',
					title: Parser.translate('landingpage.downloadFile'),
					textColor: '#000000',
					buttonText: Parser.translate('landingpage.clickToDownload'),
					buttonTextColor: '#ffffff',
					buttonBackgroundColor: '#002f65',
					asImage: false,
					imgSrc: ''
				};
				break;
			case 'map':
				elemConf.value = {
					address: '',
					lat: 0,
					lng: 0,
					showAddress: true,
					showTools: true,
					allowScrollZoom: false,
					openMapsOnClick: false
				};
				break;
			case 'landingPage':
				elemConf.value = {
					href: ''
				};
				break;
			case 'card':
				elemConf.value = {
					imgSrc: 'img/fa-user.png',
					imgOpacity: 100,
					imgSize: 'cover',
					title: 'Lorem ipsum',
					subtitle: 'Dolor',
					text: 'Sit amet',
					backgroundColor: '#ffffff',
					textColor: '#6B7C93',
					titleColor: '#000000'
				};
				break;
			case 'links':
				elemConf.value = {
					options: []
				};
				break;
			case 'share':
				elemConf.value = {
					text: '<p>' + Parser.translate('landingpage.share') + '</p>',
					twitter: { color: '#1DA1F2', active: true },
					facebook: { color: '#3C5A99', active: true },
					linkedin: { color: '#0077B5', active: true },
					align: 'center',
					size: '24px'
				};
				break;
			case 'html':
				elemConf.value = {
					html: ''
				};
				break;
		}

		return elemConf;
	}

	function setColumnType(rowId, columnId, config, type, accountProfile, callback) {
		if (!callback) {
			callback = accountProfile;
			accountProfile = null;
		}
		var rowConfig = _.find(config.rows, { id: parseInt(rowId) });
		var colConfig = _.find(rowConfig.columns, { id: parseInt(columnId) });
		var dom = jQuery('<div></div>');
		dom.append(config.markdown);

		var row = dom.find('[data-row-id=' + rowConfig.id + ']');
		var col = row.find('[data-col-id=' + colConfig.id + ']');
		var elemConf = getElementConfig(type);

		var element;
		if (type === 'image') {
			element = jQuery('<img />');
		} else {
			element = jQuery('<div />');
		}
		element.attr({
			'data-up-element': type,
			'data-up-id': elemConf.id
		});

		col.append(element);
		colConfig.elements.push(elemConf);

		config.markdown = dom.html();

		if (type === 'form') {
			config.hasForm = true;
		}

		if (type !== 'map' || !accountProfile) {
			return callback(null, config);
		}

		loader.load().then(
			() => {
				if (!geocoder) {
					geocoder = new google.maps.Geocoder();
				}

				var qs =
					accountProfile.address +
					' ' +
					accountProfile.zipcode +
					' ' +
					accountProfile.city +
					' ' +
					accountProfile.country;

				// Query google if we have a querystring
				if (qs && qs.length) {
					geocoder.geocode({ address: qs }, function (results) {
						if (results && results.length) {
							var location = results[0].geometry.location;
							elemConf.value.address = results[0].formatted_address;
							elemConf.value.lat = location.lat();
							elemConf.value.lng = location.lng();
						}
						callback(null, config);
					});
				} else {
					callback(null, config);
				}
			},
			e => {
				logError(e, 'Failed to load Google');
				callback(null, config);
			}
		);
	}

	var canSwapWithOtherRow = function (other) {
		return other.length && other.attr('data-row-id') !== undefined && other.attr('data-up-special') === undefined;
	};

	function moveRow(direction) {
		return function (config, rowId) {
			var dom = jQuery('<div></div>');
			dom.append(config.markdown);

			var row = dom.find('[data-row-id=' + rowId + ']');

			if (direction === 'down') {
				var next = row.next();
				if (canSwapWithOtherRow(next)) {
					next.after(row);
				}
			} else {
				var prev = row.prev();
				if (canSwapWithOtherRow(prev)) {
					prev.before(row);
				}
			}

			// Sort dem rows
			var rows = getRows(dom);

			rows.each(function (i, row) {
				row = jQuery(row);
				var id = row.data('row-id');
				var rowConfig = _.find(config.rows, { id: id });
				rowConfig.index = i;
			});

			config.markdown = dom.html();
			return config;
		};
	}

	// The Angular service was never instantiated under jest, so tests keep the inert defaults above.
	// eslint-disable-next-line no-undef
	var isTest = process.env.NODE_ENV === 'test';
	var businessEmailRequested = false;

	// Deferred to the first parse: App/legacy/AppService imports this module transitively, so it is still undefined at import time.
	function loadBusinessEmail() {
		if (isTest || businessEmailRequested) {
			return;
		}
		businessEmailRequested = true;

		AppService.loadedPromise
			.then(function () {
				BusinessEmail.getScript()
					.then(function (script) {
						if (script) {
							Parser.businessEmailScript = script;
						}
					})
					.catch(e => logError(e, e.message));

				var getInvalidDomains = function () {
					BusinessEmail.get()
						.then(function (res) {
							if (res.data) {
								Parser.invalidDomains = res.data.value || DEFAULT_INVALID_DOMAINS;
							}
						})
						.catch(e => logError(e, e.message));
				};
				getInvalidDomains();
				EventBus.on('businessEmail.added', function () {
					getInvalidDomains();
				});
			})
			.catch(e => logError(e, e.message));
	}

	if (!isTest) {
		Parser.translate = function (tag, opts) {
			return T(tag, opts);
		};
		Parser.URL = window.location.origin + appConfig.URL;
	}

	return Parser;
}).call(this);

export default templateParser;
